import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Icon,
  Modal,
  Radio,
  Row,
  Select,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message,
} from 'antd';
import moment from 'moment';
import * as echarts from 'echarts';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { apiGet, apiPost } from './utils/apiService';
import RegisterReconciliationTab from './components/registerRecon/RegisterReconciliationTab';
import {
  ExportableTable,
  downloadCsv,
  enumFilter,
  fmtL,
  fmtMeter,
  fmtMoney,
  numSorter,
  searchProps,
  strSorter,
  toNum,
} from './utils/tableKit';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

/*
 * Fuel Stock & Issue Report
 * ---------------------------------
 * Shows, for a selected period and fuel type: overall + tank/bowser-wise opening,
 * received, issued and closing fuel balances; asset-wise fuel issues; fuel
 * receipts; raw transactions; reconciliation; exceptions; and an interactive
 * Sankey fuel-flow chart.
 *
 * DATA SOURCING (read before changing any calculation)
 * ====================================================
 * Two live APIs are called once per fetch (in parallel) and everything is
 * derived/filtered on the client — no per-row calls (see AssetPro API rules).
 *
 *   1. POST /fuel/bowser-report  — SOURCE OF TRUTH for storage stock movement.
 *      Returns a map keyed by storage (tank/bowser) asset id. Each entry has:
 *        data.bowser           — the tank/bowser asset (assetCode, altName,
 *                                 ownerCode, location id, equipmentSubType id…)
 *        data.openingBalance   — opening stock for the tank (authoritative)
 *        data.balanceAtEnd     — closing stock for the tank (authoritative)
 *        data.totalAdded       — fuel RECEIVED into the tank in the period
 *        data.totalIssued      — fuel ISSUED from the tank in the period
 *        records[]             — every movement, each carrying the linked
 *                                refAsset (receiving asset) and refFuelRecord
 *                                (the underlying fuel entry: typeOfFuel, rate,
 *                                voucherNo, receiptNumber, isFilledToFull,
 *                                mileage[].isFaultyMeter, createdBy …).
 *                                bowserRefuelingType === 'REFILL'   -> receipt
 *                                bowserRefuelingType === 'DISPENSE' -> issue
 *
 *      WHY this is the issue/receipt source (and NOT mileage-report):
 *      The /fuel/mileage-report response does NOT carry a source-bowser link on
 *      an issue entry — it only carries a boolean `isBowser`. There is therefore
 *      no reliable, exact way to attribute a mileage issue back to the tank it
 *      came from. The bowser-report records DO carry that exact linkage
 *      (record.bowser + record.refAsset), so per-tank "received" / "issued" and
 *      the storage -> asset Sankey links are built from bowser-report. This
 *      avoids any fuzzy/guessed bowser attribution (see no-fallback rules).
 *
 *   3. MASTER DATA (fetched once per session, then reused — stable reference
 *      data, see the AssetPro API-efficiency rules):
 *        GET /asset/asset-all  — the tank's OWN identity. The bowser-report map
 *          key IS the tank's asset id, so this is an exact id match. It supplies
 *          assetCode / altName / location / model when bowser-report's `bowser`
 *          object does not: on a period with NO movement for a tank the report
 *          used to show that tank as "Unspecified" location and "Unspecified"
 *          fuel, because both were derived from the period's transactions.
 *        GET /fuel-type — id -> name for the tank's configured fuel type
 *          (assetModel.fuelType is a bare id).
 *        GET /client — id -> name for sub-locations; a fuel entry's `client` is
 *          often returned as a bare ObjectId, which no other response resolves.
 *      If any of these calls fails the report still renders the fuel data, the
 *      affected labels show as (unresolved), and a warning is shown. Nothing is
 *      guessed, and the failed lookups are retried on the next Fetch.
 *
 *   2. POST /fuel/mileage-report — used for:
 *        - "Total Fuel Purchased" (market purchases, isBowser === false) using
 *          the SAME rule as the existing Fuel Purchase Report.
 *        - "Direct-to-asset" purchases (market purchase whose asset is NOT a
 *          storage tank) vs "received into storage" (asset IS a storage tank).
 *        - Resolving ids -> names: bowser-report returns raw ObjectIds for fuel
 *          type / location / asset category, while mileage-report returns fully
 *          populated objects. We build id->name maps from mileage-report and use
 *          them to label the bowser-report data. When a name cannot be resolved
 *          it is shown as a clearly-marked fallback label, never invented.
 *        - The raw "Fuel Transactions" tab.
 *        - SUB-LOCATION (client). A fuel entry's `client` field IS its
 *          sub-location — the system labels client = sub-location — and it is
 *          the ONLY place sub-location is carried. bowser-report records do not
 *          carry it, so a tank DISPENSE takes the sub-location of its own fuel
 *          entry by joining refFuelRecord._id to the mileage entry with the same
 *          _id (an exact id join — never inferred from the asset or the tank).
 *          A dispense with no matching mileage entry is "(unresolved)"; an entry
 *          whose client is null is "Unspecified". The two are kept distinct.
 *
 * RECONCILIATION
 *   Closing Balance = Opening + Received − Issued       (per tank and overall)
 *   Opening/closing come ONLY from bowser-report; they are never derived from
 *   issue transactions. NOTE: balanceAtEnd (closing) is a stored running balance,
 *   NOT a figure derived from the aggregates. It does NOT always equal
 *   Opening + Received − Issued: on the CHABOU tenant, Aug 2026, tank VFO-35
 *   reports opening 263 + added 11,690 − issued 11,645 = 308 while balanceAtEnd
 *   says 48, and the app's own bowser day-wise report ends at 168. All three come
 *   from AssetPro. The Register Reconciliation tab surfaces that disagreement
 *   rather than silently picking one; this report shows balanceAtEnd verbatim.
 *
 * DATA RELIABILITY (see global AssetPro rules)
 *   - Missing opening/closing is kept as missing (shown "—" and flagged), never
 *     shown as 0. A stored 0 is a true zero.
 *   - Missing/non-numeric quantities are excluded from totals and counted, never
 *     treated as 0.
 *   - Fuel type / source ids are matched exactly. Unresolved names are flagged.
 *   - Fuel data is business-critical: fetched fresh on every Fetch, never cached.
 */

const UNSPECIFIED = 'Unspecified';
const UNKNOWN_NAME = '(unresolved)';

// Base URL of the AssetPro web app for deep-linking a fuel voucher:
//   <base>/<fuelRecordId>  ->  the voucher view in the main app.
// NOTE: the subdomain is TENANT-SPECIFIC (this is the eco-tech-papers org).
// Update it per deployment, or move it to env/config, if this report is reused
// for another tenant — it is intentionally a single, clearly-marked constant.
const VOUCHER_VIEW_BASE = 'https://eco-tech-papers.theassetpro.com/fuel/view';
const voucherUrl = (id) => (id ? `${VOUCHER_VIEW_BASE}/${id}` : null);

// Tolerance (litres) for cross-checking server aggregates vs summed records.
const AGGREGATE_TOLERANCE_L = 1;

const DEFAULT_RANGE = () => [moment().startOf('month'), moment().endOf('month')];

// Quick-select period presets shown as buttons next to the custom range picker.
const PERIOD_PRESETS = [
  { key: 'thisMonth', label: 'This Month', range: () => [moment().startOf('month'), moment().endOf('month')] },
  { key: 'lastMonth', label: 'Last Month', range: () => [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')] },
  // Last 3 COMPLETE calendar months (excludes the current partial month).
  // e.g. in June -> 1 Mar 00:00 to 31 May 23:59, not a rolling 3 months from today.
  { key: 'last3', label: 'Last 3 Months', range: () => [moment().subtract(3, 'months').startOf('month'), moment().subtract(1, 'month').endOf('month')] },
];

// Pull a display name out of a populated object / string reference.
const extractName = (field, fallback = '') => {
  if (!field) return fallback;
  if (typeof field === 'string') return field.trim() || fallback;
  if (typeof field === 'object') return field.name || field.code || field.title || field.altName || fallback;
  return fallback;
};

const idOf = (field) => {
  if (!field) return null;
  if (typeof field === 'string') return field;
  if (typeof field === 'object') return field._id || null;
  return null;
};

const upper = (s) => (s ? String(s).trim().toUpperCase() : s);

// Pull the array out of an apiGet envelope, across the shapes the v2 list
// endpoints use. Returns null when the CALL ITSELF failed, so a failed lookup is
// never mistaken for "no records" (see the no-fallback rules).
const extractList = (res) => {
  if (!res || res.success === false) return null;
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.data)) return d.data;
  if (d && Array.isArray(d.assets)) return d.assets;
  if (d && Array.isArray(d.rows)) return d.rows;
  return [];
};

// Resolve a reference field that may be EITHER a populated object OR a bare
// ObjectId. AssetPro is inconsistent about this: /fuel/mileage-report populates
// `location`, /asset/asset-all returns the same field as a raw id, and `client`
// is a raw id almost everywhere. Never pass such a field through extractName —
// for a string it returns the string, i.e. the raw ObjectId shown as a name.
//   - field absent/null -> whenAbsent (UNSPECIFIED by default)
//   - populated object  -> its name
//   - bare ObjectId     -> the id->name map ONLY; an id that cannot be resolved
//                          shows as (unresolved), never as the raw id
const resolveRefName = (field, nameById = {}, whenAbsent = UNSPECIFIED) => {
  if (!field) return whenAbsent;
  if (typeof field === 'object') return extractName(field, '') || UNKNOWN_NAME;
  return nameById[field] || UNKNOWN_NAME;
};

// Sub-location of a fuel entry: AssetPro stores it as the entry's "client"
// reference (the UI labels client = sub-location); some payloads spell it
// `subLocation`. Both are the same field, so reading either is not a guess.
const subLocationOf = (field, nameById) => resolveRefName(field, nameById);

const matchMulti = (selected, value) => !selected.length || selected.includes(value);

// ---------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------

/**
 * Build id->name lookup maps and a flat, normalised list of every mileage-report
 * fuel entry. The maps cover EVERY returned entry (so names resolve even for
 * out-of-window rows the API includes for consumption maths); the row list keeps
 * datetime/source so callers can strictly re-filter by window where needed.
 */
const normalizeMileage = (entries, refData = {}) => {
  const masterClientNameById = refData.clientNameById || {};
  const fuelTypeNameById = {};
  // Seeded from the /location master list so a bare location id on any entry
  // still resolves to a name instead of being printed as an id.
  const locationNameById = { ...(refData.locationNameById || {}) };
  // client id -> sub-location name. Seeded from the /client master list because
  // a fuel entry's `client` frequently comes back as a bare ObjectId that no
  // other part of either fuel response resolves.
  const subLocationNameById = { ...masterClientNameById };
  const subTypeNameById = {}; // equipmentSubType id -> category name
  const assetMetaById = {};
  const rows = [];

  entries.forEach((entry, i) => {
    const ftId = idOf(entry.typeOfFuel);
    if (ftId && typeof entry.typeOfFuel === 'object' && entry.typeOfFuel.name) {
      fuelTypeNameById[ftId] = entry.typeOfFuel.name;
    }
    const locId = idOf(entry.location);
    if (locId && typeof entry.location === 'object' && entry.location.name) {
      locationNameById[locId] = entry.location.name;
    }
    // Sub-location is the entry's `client` (see subLocationOf).
    const clientField = entry.subLocation || entry.client || null;
    const clientId = idOf(clientField);
    if (clientId && typeof clientField === 'object') {
      const clientName = extractName(clientField, '');
      if (clientName) subLocationNameById[clientId] = clientName;
    }

    const subId = idOf(entry.assetSubType);
    if (subId && typeof entry.assetSubType === 'object' && entry.assetSubType.name) {
      subTypeNameById[subId] = entry.assetSubType.name;
    }

    const assetObj = entry.asset && typeof entry.asset === 'object' ? entry.asset : null;
    const assetId = assetObj ? assetObj._id : null;
    const meta = {
      assetId,
      assetCode: (assetObj && assetObj.assetCode) || entry.assetCode || '',
      assetName: (assetObj && (assetObj.altName || assetObj.description)) || '',
      ownerCode: (assetObj && assetObj.ownerCode) || entry.ownerCode || '',
      assetTypeName: extractName(entry.assetType, ''),
      categoryName: extractName(entry.assetSubType, ''),
      modelName: extractName(entry.assetModel, ''),
      // Raw ref; resolved to a name after every entry has been seen (below).
      locationRef: entry.location,
      equipmentSubTypeId: assetObj ? idOf(assetObj.equipmentSubType) : null,
    };
    if (assetId && !assetMetaById[assetId]) assetMetaById[assetId] = meta;

    // Exact source classification — never assume market when the flag is absent.
    let source = 'UNKNOWN';
    if (entry.isBowser === true) source = 'BOWSER_ISSUE';
    else if (entry.isBowser === false) source = 'MARKET';

    const datetimeMs = entry.datetime
      ? Number(entry.datetime)
      : entry.date
      ? moment(entry.date).valueOf()
      : null;

    rows.push({
      key: entry._id || `m-${i}`,
      id: entry._id || null, // fuel record id — used for the voucher deep-link
      photos: Array.isArray(entry.photos)
        ? entry.photos.filter((p) => p && p.url).map((p) => ({ name: p.name || '', url: p.url }))
        : [],
      datetimeMs,
      assetId,
      assetCode: meta.assetCode || UNSPECIFIED,
      assetName: meta.assetName,
      ownerCode: meta.ownerCode,
      assetType: meta.assetTypeName || UNSPECIFIED,
      category: meta.categoryName || UNSPECIFIED,
      locationRef: entry.location,
      subLocationRef: clientField,
      fuelType: upper(extractName(entry.typeOfFuel, '')) || UNSPECIFIED,
      quantity: toNum(entry.quantity),
      rate: toNum(entry.rate),
      isBowser: entry.isBowser === true,
      source,
      isMarket: source === 'MARKET',
      voucherNo: entry.voucherNo || '',
      receiptNumber: entry.receiptNumber || '',
      isFaultyMeter: Array.isArray(entry.mileage)
        ? entry.mileage.some((m) => m && m.isFaultyMeter === true)
        : null,
      // Per-meter CURRENT reading (mileage[].reading) — an asset can have several
      // meters (multi-meter), so this is a list. We deliberately show the current
      // reading, NOT the cumulative one (meterReadingRecord.calculatedReading).
      meters: Array.isArray(entry.mileage)
        ? entry.mileage
            .filter((m) => m && m.reading !== null && m.reading !== undefined && m.reading !== '')
            .map((m) => ({ name: m.name || '', reading: toNum(m.reading), faulty: m.isFaultyMeter === true }))
        : [],
      isFilledToFull: entry.isFilledToFull === true ? true : entry.isFilledToFull === false ? false : null,
    });
  });

  // Resolve sub-locations only after every entry has been seen — a bare client
  // id on one entry is resolved by a populated client object on another.
  // subLocationByFuelId lets bowser-report dispenses inherit the sub-location of
  // their own fuel entry by exact id (see normalizeBowser).
  const subLocationByFuelId = {};
  rows.forEach((r) => {
    r.subLocation = subLocationOf(r.subLocationRef, subLocationNameById);
    r.locationName = resolveRefName(r.locationRef, locationNameById);
    delete r.subLocationRef;
    delete r.locationRef;
    if (r.id) subLocationByFuelId[r.id] = r.subLocation;
  });
  Object.keys(assetMetaById).forEach((id) => {
    const m = assetMetaById[id];
    m.locationName = resolveRefName(m.locationRef, locationNameById, '');
    delete m.locationRef;
  });

  return {
    fuelTypeNameById,
    locationNameById,
    subLocationNameById,
    subLocationByFuelId,
    subTypeNameById,
    assetMetaById,
    rows,
  };
};

/**
 * Normalise the bowser-report map into storages[] + issueRows[] + receiptRows[],
 * resolving ids to names via the mileage-report maps. Opening/closing/received/
 * issued are taken verbatim from the server aggregates (authoritative).
 */
const normalizeBowser = (data, maps, refData = {}) => {
  const { fuelTypeNameById, locationNameById, subLocationByFuelId, subTypeNameById, assetMetaById } = maps;
  const assetById = refData.assetById || {};
  const masterFuelTypeNameById = refData.fuelTypeNameById || {};
  // Mileage-derived names first, then the /location master list — both are
  // id->name only; an unresolvable id is reported, never printed.
  const allLocationNameById = { ...(refData.locationNameById || {}), ...locationNameById };
  // A tank's sub-location is its `client` reference, resolved through the same
  // /client master the mileage side uses.
  const clientNameById = refData.clientNameById || {};
  const storages = [];
  const issueRows = [];
  const receiptRows = [];
  // fuel record id -> the bowser/tank it was DISPENSED from. This is the
  // authoritative link from a mileage transaction (its _id == refFuelRecord._id)
  // back to its source bowser, which the mileage response itself does not carry.
  const issueSourceByFuelId = {};

  Object.keys(data || {}).forEach((storageId) => {
    const entry = data[storageId] || {};
    const meta = entry.data || {};
    const bowser = meta.bowser || {};
    const records = Array.isArray(entry.records) ? entry.records : [];

    // The bowser-report map key IS the tank's asset id, so the asset master is
    // an EXACT id match — not a name/fuzzy match. It fills in whatever the
    // bowser-report `bowser` object leaves out, which is what previously made a
    // tank with no movement in the period show as Unspecified location / fuel.
    const masterAsset = assetById[storageId] || null;

    // A tank holds one fuel type; read it from the first record that has one.
    let fuelTypeId = null;
    for (let i = 0; i < records.length && !fuelTypeId; i += 1) {
      const fr = records[i] && records[i].refFuelRecord;
      if (fr && fr.typeOfFuel) fuelTypeId = idOf(fr.typeOfFuel);
    }
    // The tank's CONFIGURED fuel type, from its asset model (master data).
    // assetModel.fuelType is a bare id, so it is named via the /fuel-type list.
    // Only a POPULATED model carries fuelType. If asset-all ever returns the
    // model as a bare id there is nothing to read, and the tank is reported as
    // fuel-type-unresolved rather than being labelled from somewhere else.
    const modelObj = masterAsset && masterAsset.assetModel && typeof masterAsset.assetModel === 'object'
      ? masterAsset.assetModel
      : null;
    const modelFuelTypeId = modelObj ? idOf(modelObj.fuelType) : null;
    const modelFuelTypeName = modelFuelTypeId
      ? upper(masterFuelTypeNameById[modelFuelTypeId] || fuelTypeNameById[modelFuelTypeId] || '') || null
      : null;
    // Movement in the period is the primary source. With no movement there is
    // nothing to read, so the tank is labelled with its configured fuel type and
    // fuelTypeSource records WHERE the label came from — the UI marks it, so a
    // configured type is never passed off as an observed one.
    let fuelTypeSource = null;
    let fuelTypeName = UNSPECIFIED;
    if (fuelTypeId) {
      fuelTypeName = upper(fuelTypeNameById[fuelTypeId] || UNKNOWN_NAME);
      fuelTypeSource = 'records';
    } else if (modelFuelTypeName) {
      fuelTypeName = modelFuelTypeName;
      fuelTypeSource = 'asset model';
    }

    // Location: try the bowser-report reference, then the tank asset's own.
    // Either can be a populated object or a bare id, so both go through
    // resolveRefName — /asset/asset-all returns `location` as a RAW id, and
    // printing that id as a location name is what this guards against.
    const locationId = idOf(bowser.location) || (masterAsset ? idOf(masterAsset.location) : null);
    const fromBowser = resolveRefName(bowser.location, allLocationNameById, '');
    const fromMaster = masterAsset ? resolveRefName(masterAsset.location, allLocationNameById, '') : '';
    const locationName =
      (fromBowser && fromBowser !== UNKNOWN_NAME && fromBowser) ||
      (fromMaster && fromMaster !== UNKNOWN_NAME && fromMaster) ||
      (locationId ? UNKNOWN_NAME : UNSPECIFIED);

    // A tank is allocated to a location AND a sub-location (client), so it is
    // valid to narrow it by that pair. Resolved by exact id from the bowser
    // record first, then the asset master — never inferred from its movements.
    const subLocationId = idOf(bowser.client) || (masterAsset ? idOf(masterAsset.client) : null);
    const subFromBowser = resolveRefName(bowser.client, clientNameById, '');
    const subFromMaster = masterAsset ? resolveRefName(masterAsset.client, clientNameById, '') : '';
    const subLocation =
      (subFromBowser && subFromBowser !== UNKNOWN_NAME && subFromBowser) ||
      (subFromMaster && subFromMaster !== UNKNOWN_NAME && subFromMaster) ||
      (subLocationId ? UNKNOWN_NAME : UNSPECIFIED);

    const assetCode = bowser.assetCode || (masterAsset && masterAsset.assetCode) || UNSPECIFIED;
    const altName =
      (bowser.altName && bowser.altName.trim()) ||
      (masterAsset && masterAsset.altName && String(masterAsset.altName).trim()) ||
      '';
    const ownerCode = bowser.ownerCode || (masterAsset && masterAsset.ownerCode) || '';
    const storageName = altName || (assetCode !== UNSPECIFIED ? assetCode : storageId);

    let recordReceived = 0;
    let recordIssued = 0;

    records.forEach((record, ri) => {
      const fr = record.refFuelRecord || {};
      const recFtId = idOf(fr.typeOfFuel);
      const recFt = recFtId ? upper(fuelTypeNameById[recFtId] || UNKNOWN_NAME) : fuelTypeName;
      const added = toNum(record.quantityAdded) || 0;
      const removed = toNum(record.quantityRemoved) || 0;

      if (removed > 0) {
        // DISPENSE — fuel issued from this tank to refAsset.
        recordIssued += removed;
        if (fr._id) issueSourceByFuelId[fr._id] = { storageId, storageName, assetCode: assetCode === UNSPECIFIED ? '' : assetCode };
        const ra = record.refAsset && typeof record.refAsset === 'object' ? record.refAsset : {};
        const assetId = ra._id || null;
        const am = (assetId && assetMetaById[assetId]) || {};
        const category =
          subTypeNameById[idOf(ra.equipmentSubType)] || am.categoryName || UNSPECIFIED;
        issueRows.push({
          key: record._id || `i-${storageId}-${ri}`,
          datetimeMs: Number(record.datetime) || (record.date ? moment(record.date).valueOf() : null),
          // AssetPro's own running balance AFTER this movement, and the record's
          // position in the response. Several records can share a timestamp, so
          // the position is what makes "the last record of the day" well defined.
          balanceAfter: toNum(record.balance),
          seq: ri,
          storageId,
          storageName,
          assetId,
          assetCode: ra.assetCode || am.assetCode || UNSPECIFIED,
          assetName: am.assetName || (ra.altName && ra.altName.trim()) || '',
          ownerCode: am.ownerCode || ra.ownerCode || '',
          assetType: am.assetTypeName || UNSPECIFIED,
          category: category || UNSPECIFIED,
          locationName: am.locationName || locationName,
          // Exact id join to the dispense's own fuel entry (the only carrier of
          // sub-location). No matching mileage entry -> unresolved, not blank.
          subLocation: fr._id && subLocationByFuelId[fr._id] !== undefined
            ? subLocationByFuelId[fr._id]
            : UNKNOWN_NAME,
          fuelType: recFt,
          quantity: removed,
          rate: toNum(fr.rate),
          voucherNo: fr.voucherNo || '',
          isFaultyMeter: Array.isArray(fr.mileage)
            ? fr.mileage.some((m) => m && m.isFaultyMeter === true)
            : null,
          isFilledToFull: fr.isFilledToFull === true,
          createdBy: fr.createdBy || '',
        });
      } else if (added > 0) {
        // REFILL — fuel received into this tank.
        recordReceived += added;
        receiptRows.push({
          key: record._id || `r-${storageId}-${ri}`,
          datetimeMs: Number(record.datetime) || (record.date ? moment(record.date).valueOf() : null),
          balanceAfter: toNum(record.balance),
          seq: ri,
          storageId,
          storageName,
          fuelType: recFt,
          quantity: added,
          rate: toNum(fr.rate),
          voucherNo: fr.voucherNo || '',
          receiptNumber: fr.receiptNumber || '',
          createdBy: fr.createdBy || '',
          locationName,
        });
      }
    });

    storages.push({
      storageId,
      assetCode,
      storageName,
      ownerCode,
      locationId,
      locationName,
      subLocationId,
      subLocation,
      fuelTypeId,
      fuelType: fuelTypeName,
      // 'records' | 'asset model' | null — where the fuel-type label came from.
      fuelTypeSource,
      // The tank's configured fuel type, kept so a disagreement with the movements
      // can be flagged instead of silently preferring one of them.
      modelFuelType: modelFuelTypeName,
      // True when the tank asset itself could not be found in the master list, so
      // the UI can say the labels are unverified rather than implying they are.
      masterMissing: !masterAsset,
      // Authoritative server aggregates. null (missing) is preserved distinctly
      // from a real 0 so the UI can flag it rather than show a false zero.
      opening: toNum(meta.openingBalance),
      closing: toNum(meta.balanceAtEnd),
      received: toNum(meta.totalAdded),
      issued: toNum(meta.totalIssued),
      // Sums of the underlying records, for cross-checking the aggregates.
      recordReceived,
      recordIssued,
      recordCount: records.length,
    });
  });

  return { storages, issueRows, receiptRows, issueSourceByFuelId };
};

const uniqSorted = (arr) => [...new Set(arr.filter((v) => v && v !== UNSPECIFIED))].sort();

// Movement-type colours shared by the Sankey and its legend.
const MOVEMENT_COLORS = {
  OPENING: '#13c2c2',
  RECEIPT_TO_STORAGE: '#52c41a',
  ISSUE: '#1890ff',
  DIRECT: '#722ed1',
  CLOSING_STOCK: '#8c8c8c',
};

// Single shared node name so "long tail" assets merge into one bucket across
// every storage unit and the direct-purchase line.
const OTHER_ASSETS = 'Other Assets';
const TOP_ASSETS = 25;

function FuelStockIssueReport() {
  const [range, setRange] = useState(DEFAULT_RANGE());
  const [periodPreset, setPeriodPreset] = useState('thisMonth'); // '' = custom
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [queriedRange, setQueriedRange] = useState(null);

  // Raw normalised data (kept verbatim; filtering happens in derived memos).
  const [storages, setStorages] = useState([]);
  const [issueRows, setIssueRows] = useState([]);
  const [receiptRows, setReceiptRows] = useState([]);
  const [mileageRows, setMileageRows] = useState([]);
  const [fetchNotes, setFetchNotes] = useState(null);
  // Last reference-data load (assets / fuel types / clients / locations). Held in
  // state as well as the ref so the Register Reconciliation tab can use whatever
  // DID load even when the load was partial (a partial load is never cached).
  const [refData, setRefData] = useState(null);

  // Filters
  const [fFuelType, setFFuelType] = useState(null); // single (defaulted to Diesel)
  const [fLocation, setFLocation] = useState([]);
  const [fSubLocation, setFSubLocation] = useState([]); // sub-location = client
  const [fStorage, setFStorage] = useState([]);
  const [fAssetType, setFAssetType] = useState([]);
  const [fAsset, setFAsset] = useState([]);

  // Sankey + interactions
  const [sankeyMode, setSankeyMode] = useState('GROUPED'); // TOTAL | GROUPED | ASSET
  const [sankeyFullscreen, setSankeyFullscreen] = useState(false);
  const [activeTab, setActiveTab] = useState('storage');
  const [assetDrawer, setAssetDrawer] = useState(null); // { assetId }
  const [photoModal, setPhotoModal] = useState(null); // { voucherNo, photos }

  const chartDivRef = useRef(null);
  const chartRef = useRef(null);

  // Master/reference data (assets, fuel types, clients). Stable reference data,
  // so it is fetched ONCE per session and reused by every later Fetch — only the
  // two fuel endpoints are re-called, because fuel data is business-critical and
  // must stay fresh. A partial/failed load is NOT cached, so it retries.
  const refDataRef = useRef(null);

  // Load (once) the master lists the fuel endpoints do not resolve themselves:
  //   /asset/asset-all — tank identity by exact asset id (code, altName, location,
  //                      model) for tanks the bowser-report returns thinly
  //   /fuel-type       — names the tank model's bare fuelType id
  //   /client          — names the bare client ids that carry sub-location
  //   /location        — names location ids; asset-all returns `location` as a
  //                      RAW id, so without this a tank's location would be an id
  // Returns { assetById, fuelTypeNameById, clientNameById, locationNameById,
  //           failed: [names] }.
  // A failed list is reported, never substituted — affected labels stay
  // (unresolved) and the call is retried on the next Fetch.
  const loadReferenceData = useCallback(async () => {
    if (refDataRef.current) return refDataRef.current;

    const opts = { headers: { Accept: '*/*' } };
    const [assetRes, fuelTypeRes, clientRes, locationRes] = await Promise.all([
      apiGet('/asset/asset-all', opts),
      apiGet('/fuel-type', opts),
      apiGet('/client', opts),
      apiGet('/location', opts),
    ]);

    const assets = extractList(assetRes);
    const fuelTypes = extractList(fuelTypeRes);
    const clients = extractList(clientRes);
    const locations = extractList(locationRes);

    const failed = [];
    if (assets === null) failed.push('assets (/asset/asset-all)');
    if (fuelTypes === null) failed.push('fuel types (/fuel-type)');
    if (clients === null) failed.push('clients (/client)');
    if (locations === null) failed.push('locations (/location)');

    const assetById = {};
    (assets || []).forEach((a) => {
      if (a && a._id) assetById[a._id] = a;
    });
    const fuelTypeNameById = {};
    (fuelTypes || []).forEach((f) => {
      if (f && f._id && f.name) fuelTypeNameById[f._id] = f.name;
    });
    const clientNameById = {};
    (clients || []).forEach((c) => {
      if (c && c._id) {
        const name = c.name || c.code || '';
        if (name) clientNameById[c._id] = name;
      }
    });

    const locationNameById = {};
    (locations || []).forEach((l) => {
      if (l && l._id) {
        const name = l.name || l.code || '';
        if (name) locationNameById[l._id] = name;
      }
    });

    const refData = { assetById, fuelTypeNameById, clientNameById, locationNameById, failed };
    // Only a complete load is cached; a partial one must not become permanent.
    if (!failed.length) refDataRef.current = refData;
    return refData;
  }, []);

  // --- Fetch --------------------------------------------------------------
  const handleFetch = useCallback(async () => {
    if (!range || !range[0] || !range[1]) {
      message.warning('Pick a date range.');
      return;
    }
    const from = range[0].clone().startOf('day');
    const to = range[1].clone().endOf('day');

    setLoading(true);
    setError(null);
    setStorages([]);
    setIssueRows([]);
    setReceiptRows([]);
    setMileageRows([]);
    setFetchNotes(null);

    try {
      // Same UI filters into each endpoint's own payload shape. Both fetch the
      // full period and we filter on the client (fetch once, reuse).
      const bowserPayload = {
        locations: [],
        subLocations: [],
        assetTypes: [],
        assets: [],
        periodFrom: from.valueOf(),
        periodTo: to.valueOf(),
      };
      const mileagePayload = {
        asset: null,
        location: 'all',
        subLocation: 'all',
        periodFrom: from.toISOString(),
        periodTo: to.toISOString(),
      };

      const [bowserRes, mileageRes, loadedRefData] = await Promise.all([
        apiPost('/fuel/bowser-report', bowserPayload),
        apiPost('/fuel/mileage-report', mileagePayload),
        loadReferenceData(),
      ]);
      const refData = loadedRefData;
      setRefData(loadedRefData);

      if (!bowserRes || bowserRes.success === false) {
        throw new Error((bowserRes && bowserRes.error) || 'Failed to fetch bowser report');
      }
      if (!mileageRes || mileageRes.success === false) {
        throw new Error((mileageRes && mileageRes.error) || 'Failed to fetch mileage report');
      }

      const mileageEntries = Array.isArray(mileageRes.data) ? mileageRes.data : [];
      const maps = normalizeMileage(mileageEntries, refData);

      const bowserData = bowserRes.data || {};
      const { storages: st, issueRows: ir, receiptRows: rr, issueSourceByFuelId } = normalizeBowser(bowserData, maps, refData);

      // Join each transaction back to the bowser it was issued from (only bowser
      // issues have one). The mileage row's _id == the dispense's refFuelRecord._id.
      const enrichedRows = maps.rows.map((r) => {
        const src = r.id ? issueSourceByFuelId[r.id] : null;
        return {
          ...r,
          // Resolved source bowser asset code for bowser issues; '' otherwise.
          // A bowser issue with no match is flagged as unresolved, never guessed.
          bowserSourceCode: src ? src.assetCode || UNKNOWN_NAME : r.isBowser ? UNKNOWN_NAME : '',
          bowserSourceName: src ? src.storageName : '',
        };
      });

      setStorages(st);
      setIssueRows(ir);
      setReceiptRows(rr);
      setMileageRows(enrichedRows);
      setQueriedRange([from, to]);

      // Default fuel type to Diesel when present, else the first available.
      const fuelTypes = uniqSorted(st.map((s) => s.fuelType));
      const diesel = fuelTypes.find((f) => f && f.includes('DIESEL'));
      setFFuelType(diesel || fuelTypes[0] || null);

      // Reset interaction state on a fresh fetch.
      setFLocation([]);
      setFSubLocation([]);
      setFStorage([]);
      setFAssetType([]);
      setFAsset([]);

      const notes = [];
      if (st.length === 0) notes.push('No storage tanks / bowsers were returned for this period.');
      if (refData.failed.length) {
        notes.push(
          `Master data could not be loaded: ${refData.failed.join(', ')}. Tank location / fuel type / sub-location labels that depend on it show as ${UNKNOWN_NAME}; the fuel figures themselves are unaffected. It will be retried on the next Fetch.`
        );
      }
      const tanksWithoutMaster = st.filter((x) => x.masterMissing).length;
      if (tanksWithoutMaster > 0 && !refData.failed.length) {
        notes.push(
          `${tanksWithoutMaster} storage tank(s) were not found in the asset master, so their location / fuel type could only be taken from the period's movements.`
        );
      }
      setFetchNotes(notes.length ? notes.join(' ') : null);
      if (st.length === 0) message.info('No bowser/tank data for the selected period.');
    } catch (e) {
      console.error('[FuelStockIssueReport] fetch failed', e);
      setError(e.message || 'Failed to load the fuel stock & issue report');
    } finally {
      setLoading(false);
    }
  }, [range, loadReferenceData]);

  // --- Derived: filter option lists --------------------------------------
  // Assets that consume fuel (any non-storage asset seen in the mileage data),
  // so the Asset / Asset Type / Location filters also list assets that ONLY
  // received fuel directly (mileage) and never via a bowser dispense (issueRows).
  const mileageAssetRows = useMemo(() => {
    const storageIds = new Set(storages.map((s) => s.storageId));
    return mileageRows.filter((r) => r.assetId && !storageIds.has(r.assetId));
  }, [mileageRows, storages]);

  // Fuel types come from BOTH APIs — bowser tanks (storages) and mileage entries
  // — so fuels that only appear as direct purchases (e.g. Petrol) are selectable.
  const fuelTypeOptions = useMemo(
    () => uniqSorted([...storages.map((s) => s.fuelType), ...mileageRows.map((r) => r.fuelType)]),
    [storages, mileageRows]
  );
  const locationOptions = useMemo(
    () => uniqSorted([...storages.map((s) => s.locationName), ...mileageRows.map((r) => r.locationName)]),
    [storages, mileageRows]
  );
  // Sub-location (client) options — from the mileage entries and from the tank
  // dispenses (each dispense inherits its own fuel entry's client), narrowed by
  // the selected Location so the list stays relevant. "(unresolved)" is listed
  // when it occurs so those rows can be isolated rather than quietly ignored.
  const subLocationOptions = useMemo(
    () =>
      uniqSorted([
        ...mileageRows.filter((r) => matchMulti(fLocation, r.locationName)).map((r) => r.subLocation),
        ...issueRows.filter((r) => matchMulti(fLocation, r.locationName)).map((r) => r.subLocation),
      ]),
    [mileageRows, issueRows, fLocation]
  );
  const assetTypeOptions = useMemo(
    () => uniqSorted([...issueRows.map((r) => r.assetType), ...mileageAssetRows.map((r) => r.assetType)]),
    [issueRows, mileageAssetRows]
  );
  const storageChoices = useMemo(() => {
    const seen = new Map();
    storages.forEach((s) => {
      if (!seen.has(s.storageId)) seen.set(s.storageId, s);
    });
    return Array.from(seen.values()).sort((a, b) => a.storageName.localeCompare(b.storageName));
  }, [storages]);
  const assetChoices = useMemo(() => {
    const byId = new Map();
    const addRow = (r) => {
      if (r.assetId && !byId.has(r.assetId)) {
        byId.set(r.assetId, { id: r.assetId, code: r.assetCode, name: r.assetName, owner: r.ownerCode });
      }
    };
    issueRows.forEach(addRow);
    mileageAssetRows.forEach(addRow);
    return Array.from(byId.values()).sort((a, b) => String(a.code).localeCompare(String(b.code)));
  }, [issueRows, mileageAssetRows]);

  // --- Derived: filtered datasets ----------------------------------------
  const matchFuel = useCallback((ft) => !fFuelType || ft === fFuelType, [fFuelType]);

  const filteredStorages = useMemo(
    () =>
      storages
        .filter((s) => matchFuel(s.fuelType))
        .filter((s) => matchMulti(fLocation, s.locationName))
        .filter((s) => matchMulti(fStorage, s.storageId)),
    [storages, matchFuel, fLocation, fStorage]
  );

  const filteredStorageIds = useMemo(
    () => new Set(filteredStorages.map((s) => s.storageId)),
    [filteredStorages]
  );
  const allStorageIds = useMemo(() => new Set(storages.map((s) => s.storageId)), [storages]);

  // Context handed to the Register Reconciliation tab.
  //
  // IMPORTANT — this passes the RAW issue/receipt/mileage rows, not
  // `filteredIssues`/`filteredMileage`. Those already bake in the Asset Type and
  // Asset filters, which must never narrow one side of a reconciliation while
  // the other stays whole. The tab re-scopes the raw rows itself.
  // `filteredStorages` is safe to pass because it applies only fuel type,
  // location and tank.
  const reconContext = useMemo(
    () => ({
      queriedRange,
      storages,
      filteredStorages,
      allStorageIds,
      issueRows,
      receiptRows,
      mileageRows,
      filters: { fFuelType, fLocation, fStorage, fSubLocation, fAssetType, fAsset },
      refData,
    }),
    [
      queriedRange, storages, filteredStorages, allStorageIds,
      issueRows, receiptRows, mileageRows,
      fFuelType, fLocation, fStorage, fSubLocation, fAssetType, fAsset, refData,
    ]
  );

  const filteredIssues = useMemo(
    () =>
      issueRows
        .filter((r) => filteredStorageIds.has(r.storageId))
        .filter((r) => matchFuel(r.fuelType))
        .filter((r) => matchMulti(fSubLocation, r.subLocation))
        .filter((r) => matchMulti(fAssetType, r.assetType))
        .filter((r) => matchMulti(fAsset, r.assetId)),
    [issueRows, filteredStorageIds, matchFuel, fSubLocation, fAssetType, fAsset]
  );

  const filteredReceipts = useMemo(
    () =>
      receiptRows
        .filter((r) => filteredStorageIds.has(r.storageId))
        .filter((r) => matchFuel(r.fuelType)),
    [receiptRows, filteredStorageIds, matchFuel]
  );

  // Raw mileage transactions for display + the purchase KPI (strictly in-window
  // and matched to ALL active filters — fuel type, location, sub-location,
  // asset type, asset —
  // so the Receipts/Transactions tables and the direct/purchased KPIs respond to
  // the same filters as the Sankey and Fuel Tank Issues. (Asset Type / Asset
  // filter by the receiving asset, so a tank refill row is excluded once a
  // consuming-asset filter is set — its asset is the tank, not the consumer.)
  const filteredMileage = useMemo(() => {
    const fromMs = queriedRange ? queriedRange[0].valueOf() : null;
    const toMs = queriedRange ? queriedRange[1].valueOf() : null;
    return mileageRows
      .filter((r) => (fromMs === null ? true : r.datetimeMs !== null && r.datetimeMs >= fromMs && r.datetimeMs <= toMs))
      .filter((r) => matchFuel(r.fuelType))
      .filter((r) => matchMulti(fLocation, r.locationName))
      .filter((r) => matchMulti(fSubLocation, r.subLocation))
      .filter((r) => matchMulti(fAssetType, r.assetType))
      .filter((r) => matchMulti(fAsset, r.assetId));
  }, [mileageRows, queriedRange, matchFuel, fLocation, fSubLocation, fAssetType, fAsset]);

  // --- Derived: Fuel Receipts / Purchases --------------------------------
  // Every fuel RECEIPT in the period, sourced from mileage-report market
  // purchases (isBowser === false) so BOTH paths are covered:
  //   - fill whose asset IS a storage tank  -> "Into Storage" (a bowser/tank refill)
  //   - fill whose asset is NOT a tank       -> "Direct to Asset" (received directly)
  // This is why we use mileage-report here, not just the bowser-report REFILL
  // records (those only carry tank refills). Sums to "Total Purchased".
  // Unknown-source entries (isBowser missing) are NOT shown as receipts — they
  // are never assumed to be a purchase (see no-fallback rules); they remain
  // flagged in the Fuel Transactions tab.
  const receiptsTable = useMemo(() => {
    const storageById = new Map(storages.map((s) => [s.storageId, s]));
    return filteredMileage
      .filter((r) => r.isMarket)
      .filter((r) => {
        // When drilled into a specific tank, only that tank's refills apply
        // (direct-to-asset receipts do not belong to any storage).
        if (fStorage.length) return r.assetId && fStorage.includes(r.assetId);
        return true;
      })
      .map((r) => {
        const isStorage = r.assetId && allStorageIds.has(r.assetId);
        const storage = isStorage ? storageById.get(r.assetId) : null;
        return {
          key: r.key,
          datetimeMs: r.datetimeMs,
          voucherNo: r.voucherNo,
          receiptNumber: r.receiptNumber,
          fuelType: r.fuelType,
          receivedInto: isStorage ? 'Storage' : 'Direct to Asset',
          targetName: isStorage
            ? storage
              ? `${storage.storageName} (${storage.assetCode})`
              : r.assetCode
            : [r.assetCode, r.assetName].filter(Boolean).join(' · '),
          quantity: r.quantity,
          rate: r.rate,
          locationName: r.locationName,
          subLocation: r.subLocation,
          assetId: r.assetId,
        };
      });
  }, [filteredMileage, storages, allStorageIds, fStorage]);

  // --- Derived: storage summary ------------------------------------------
  const storageSummary = useMemo(
    () =>
      filteredStorages.map((s) => {
        const hasOpening = s.opening !== null;
        const hasClosing = s.closing !== null;
        const status = !hasOpening || !hasClosing ? 'Missing Opening / Closing' : 'OK';
        return {
          ...s,
          actualClosing: s.closing,
          status,
        };
      }),
    [filteredStorages]
  );

  // --- Derived: overall KPIs ---------------------------------------------
  const summary = useMemo(() => {
    let opening = 0;
    let received = 0;
    let issued = 0;
    let closing = 0;
    let missingOpening = 0;
    let missingClosing = 0;

    filteredStorages.forEach((s) => {
      if (s.opening !== null) opening += s.opening;
      else missingOpening += 1;
      if (s.closing !== null) closing += s.closing;
      else missingClosing += 1;
      if (s.received !== null) received += s.received;
      if (s.issued !== null) issued += s.issued;
    });

    // Total purchased & split (market purchases from mileage-report).
    let purchased = 0;
    let directToAsset = 0;
    let receivedIntoStorageMileage = 0;
    let missingPurchaseQty = 0;
    filteredMileage.forEach((r) => {
      if (!r.isMarket) return; // only market purchases count as "purchased"
      if (r.quantity === null) {
        missingPurchaseQty += 1;
        return;
      }
      purchased += r.quantity;
      if (r.assetId && allStorageIds.has(r.assetId)) receivedIntoStorageMileage += r.quantity;
      else directToAsset += r.quantity;
    });

    // Complete picture (per the report's "full story" summary):
    //   Total Purchased = fuel received into storage + fuel bought direct to asset
    //   Total Issued    = fuel issued from storage   + fuel bought direct to asset
    // "Received into storage" uses the authoritative bowser stock figure
    // (totalAdded) so it ties to the closing identity and the Sankey. The direct
    // component comes from mileage market purchases whose asset is not a tank
    // (the only source for fuel that never passes through a tank). The same
    // direct quantity is both a purchase and a delivery to the asset, so it
    // appears in both totals.
    const totalPurchased = received + directToAsset;
    const totalIssuedToAssets = issued + directToAsset;

    return {
      opening,
      received,
      issued,
      closing,
      missingOpening,
      missingClosing,
      purchased,
      directToAsset,
      receivedIntoStorageMileage,
      totalPurchased,
      totalIssuedToAssets,
      missingPurchaseQty,
      tankCount: filteredStorages.length,
    };
  }, [filteredStorages, filteredMileage, allStorageIds]);

  // --- Derived: Fuel Issues (per asset) ----------------------------------
  // Total fuel that reached each asset in the period = issued FROM STORAGE
  // (bowser dispenses) + issued DIRECT (market purchases straight into the
  // asset, never via a tank). Both paths are merged per asset so the asset's
  // monthly total and its from-storage / direct split are visible together.
  const assetIssues = useMemo(() => {
    const byAsset = new Map();
    const ensure = (r) => {
      const id = r.assetId || `code:${r.assetCode}`;
      if (!byAsset.has(id)) {
        byAsset.set(id, {
          key: id,
          assetId: r.assetId,
          assetCode: r.assetCode,
          assetName: r.assetName,
          ownerCode: r.ownerCode,
          assetType: r.assetType,
          category: r.category,
          locationName: r.locationName,
          fuelType: r.fuelType,
          // An asset can move between clients inside one period, so every
          // distinct sub-location seen is kept and shown — never collapsed to one.
          subLocations: new Set(),
          sources: new Set(),
          fromStorage: 0,
          direct: 0,
          amount: 0,
          rateWeighted: 0,
          rateQty: 0,
          count: 0,
          faulty: 0,
          filledToFull: 0,
        });
      }
      return byAsset.get(id);
    };
    const tallyRate = (g, qty, rate) => {
      if (rate !== null && qty) {
        g.amount += qty * rate;
        g.rateWeighted += qty * rate;
        g.rateQty += qty;
      }
    };

    // Issued from storage (bowser dispenses).
    filteredIssues.forEach((r) => {
      const g = ensure(r);
      if (r.subLocation) g.subLocations.add(r.subLocation);
      g.sources.add(r.storageName);
      g.fromStorage += r.quantity || 0;
      g.count += 1;
      tallyRate(g, r.quantity, r.rate);
      if (r.isFaultyMeter === true) g.faulty += 1;
      if (r.isFilledToFull === true) g.filledToFull += 1;
    });

    // Issued direct (market purchases into non-tank assets).
    filteredMileage
      .filter((r) => r.isMarket && (!r.assetId || !allStorageIds.has(r.assetId)))
      .forEach((r) => {
        if (r.quantity === null || r.quantity <= 0) return;
        const g = ensure(r);
        if (r.subLocation) g.subLocations.add(r.subLocation);
        g.direct += r.quantity;
        g.count += 1;
        tallyRate(g, r.quantity, r.rate);
        if (r.isFaultyMeter === true) g.faulty += 1;
        if (r.isFilledToFull === true) g.filledToFull += 1;
      });

    return Array.from(byAsset.values())
      .map((g) => ({
        ...g,
        total: g.fromStorage + g.direct,
        subLocationLabel: Array.from(g.subLocations).join(', ') || '—',
        sourceLabel: Array.from(g.sources).join(', ') || '—',
        avgRate: g.rateQty > 0 ? g.rateWeighted / g.rateQty : null,
      }))
      .sort((a, b) => b.total - a.total);
  }, [filteredIssues, filteredMileage, allStorageIds]);

  // Tab-level totals for the Fuel Issues summary caption (matches the table).
  const issuesTotals = useMemo(() => {
    let fromStorage = 0;
    let direct = 0;
    assetIssues.forEach((g) => {
      fromStorage += g.fromStorage;
      direct += g.direct;
    });
    return { fromStorage, direct, total: fromStorage + direct };
  }, [assetIssues]);

  // --- Derived: exceptions -----------------------------------------------
  const exceptions = useMemo(() => {
    const list = [];
    const add = (severity, type, detail) => list.push({ key: `${list.length}`, severity, type, detail });

    storageSummary.forEach((s) => {
      if (s.opening === null) add('High', 'Missing opening balance', `${s.storageName}: opening balance missing from bowser-report.`);
      if (s.closing === null) add('High', 'Missing closing balance', `${s.storageName}: closing balance missing from bowser-report.`);
      if (s.issued !== null && s.recordIssued > 0 && Math.abs(s.issued - s.recordIssued) > AGGREGATE_TOLERANCE_L) {
        add('Medium', 'Issued aggregate mismatch', `${s.storageName}: server totalIssued ${fmtL(s.issued)} L vs summed issue records ${fmtL(s.recordIssued)} L.`);
      }
      if (s.received !== null && s.recordReceived > 0 && Math.abs(s.received - s.recordReceived) > AGGREGATE_TOLERANCE_L) {
        add('Medium', 'Received aggregate mismatch', `${s.storageName}: server totalAdded ${fmtL(s.received)} L vs summed refill records ${fmtL(s.recordReceived)} L.`);
      }
      if (s.fuelType === UNSPECIFIED || s.fuelType === UNKNOWN_NAME) {
        add('Low', 'Fuel type unresolved', `${s.storageName}: no fuel movement in this period and no configured fuel type on its asset model${s.masterMissing ? ' (tank not found in the asset master)' : ''}.`);
      }
      // Both sources exist and disagree — surfaced rather than silently picking one.
      if (s.fuelTypeSource === 'records' && s.modelFuelType && s.modelFuelType !== s.fuelType) {
        add('Low', 'Fuel type mismatch', `${s.storageName}: movements are ${s.fuelType} but the tank's asset model is configured as ${s.modelFuelType}.`);
      }
      if (s.locationName === UNSPECIFIED || s.locationName === UNKNOWN_NAME) {
        add('Low', 'Tank location unresolved', `${s.storageName}: location missing on the bowser-report record${s.masterMissing ? ' and the tank was not found in the asset master' : ' and on the tank asset'}.`);
      }
    });

    filteredIssues.forEach((r) => {
      if (!r.assetId) add('Medium', 'Issue without asset', `Issue from ${r.storageName} (${fmtL(r.quantity)} L) has no receiving asset.`);
      if (r.quantity === null || r.quantity <= 0) add('Medium', 'Invalid issue quantity', `Issue from ${r.storageName} to ${r.assetCode} has a missing/non-positive quantity.`);
      if (r.isFaultyMeter === true) add('Medium', 'Faulty meter', `Issue to ${r.assetCode} from ${r.storageName} is marked faulty meter.`);
      if (r.rate === null) add('Low', 'Missing rate', `Issue to ${r.assetCode} from ${r.storageName} has no rate.`);
    });

    filteredReceipts.forEach((r) => {
      if (r.quantity === null || r.quantity <= 0) add('Medium', 'Invalid receipt quantity', `Receipt into ${r.storageName} (voucher ${r.voucherNo || '—'}) has a missing/non-positive quantity.`);
    });

    const order = { High: 0, Medium: 1, Low: 2 };
    return list.sort((a, b) => order[a.severity] - order[b.severity]);
  }, [storageSummary, filteredIssues, filteredReceipts]);

  // --- Derived: Sankey data (ECharts) ------------------------------------
  // Time-ordered columns pinned by an explicit `depth` per node (ECharts honours
  // node.depth as the column index, which amCharts could not do):
  //   depth 0: Opening Balance (per tank)
  //   depth 1: Fuel Purchased / Direct Purchase  (inflows)
  //   depth 2: Storage Unit (tank/bowser)
  //   depth 3: Fuel issued targets (single node / category / asset)
  //   depth 4: Closing Balance (per tank)         (after issue)
  // So opening sits left of purchased, and closing sits right of issued — and
  // each tank has its OWN opening and closing node so you can see which balance
  // belongs to which tank/fuel. Direct purchases feed the SAME issued targets,
  // bypassing storage. Returns { nodes, links, nodeMeta, otherCount }.
  const sankey = useMemo(() => {
    const nodes = [];
    const links = [];
    const nodeMeta = {};
    const seen = new Set();
    const PURCHASED = 'Fuel Purchased';
    const DIRECT = 'Direct Purchase';
    const ISSUED_TOTAL = 'Fuel Issued';

    const ensureNode = (name, depth, color, meta, labelPosition) => {
      if (!seen.has(name)) {
        seen.add(name);
        const node = { name, depth, itemStyle: { color } };
        if (labelPosition) node.label = { position: labelPosition };
        nodes.push(node);
        nodeMeta[name] = meta;
      }
      return name;
    };
    const addLink = (source, target, value, color) => {
      if (!value || value <= 0) return;
      links.push({ source, target, value, lineStyle: { color, opacity: 0.45 } });
    };

    // In ASSET mode keep only the top-N assets by total fuel (issued + direct);
    // everything else collapses into one shared "Other Assets" node so the chart
    // stays legible. The collapsed count is surfaced in the caption, never silent.
    let topAssetIds = null;
    let otherCount = 0;
    if (sankeyMode === 'ASSET') {
      const byAsset = new Map();
      const add = (id, qty) => byAsset.set(id, (byAsset.get(id) || 0) + (qty || 0));
      filteredIssues.forEach((r) => add(r.assetId || `code:${r.assetCode}`, r.quantity));
      filteredMileage
        .filter((r) => r.isMarket && r.assetId && !allStorageIds.has(r.assetId))
        .filter((r) => matchMulti(fAssetType, r.assetType))
        .filter((r) => matchMulti(fAsset, r.assetId))
        .forEach((r) => add(r.assetId || `code:${r.assetCode}`, r.quantity));
      const ordered = Array.from(byAsset.entries()).sort((a, b) => b[1] - a[1]);
      topAssetIds = new Set(ordered.slice(0, TOP_ASSETS).map(([id]) => id));
      otherCount = Math.max(0, ordered.length - TOP_ASSETS);
    }

    // Resolve an issue/direct-purchase row to its depth-3 target node.
    const targetNodeFor = (cat, assetId, assetCode) => {
      if (sankeyMode === 'TOTAL') return ensureNode(ISSUED_TOTAL, 3, MOVEMENT_COLORS.ISSUE, { type: 'ISSUED_TOTAL' });
      if (sankeyMode === 'GROUPED') return ensureNode(`▸ ${cat || UNSPECIFIED}`, 3, MOVEMENT_COLORS.ISSUE, { type: 'CATEGORY', id: cat || UNSPECIFIED });
      const id = assetId || `code:${assetCode}`;
      if (topAssetIds && !topAssetIds.has(id)) return ensureNode(OTHER_ASSETS, 3, MOVEMENT_COLORS.ISSUE, { type: 'OTHER' });
      return ensureNode(assetCode || OTHER_ASSETS, 3, MOVEMENT_COLORS.ISSUE, { type: 'ASSET', id });
    };

    // Group issues per storage.
    const issuesByStorage = new Map();
    filteredIssues.forEach((r) => {
      if (!issuesByStorage.has(r.storageId)) issuesByStorage.set(r.storageId, []);
      issuesByStorage.get(r.storageId).push(r);
    });

    storageSummary.forEach((s) => {
      const storageNode = ensureNode(s.storageName, 2, '#5b6b7f', { type: 'STORAGE', id: s.storageId });

      // depth 0/1 inflows: this tank's opening balance, then fuel purchased.
      if (s.opening) {
        const open = ensureNode(`Opening · ${s.storageName}`, 0, MOVEMENT_COLORS.OPENING, { type: 'OPENING', id: s.storageId });
        addLink(open, storageNode, s.opening, MOVEMENT_COLORS.OPENING);
      }
      if (s.received) {
        ensureNode(PURCHASED, 1, MOVEMENT_COLORS.RECEIPT_TO_STORAGE, { type: 'PURCHASED' });
        addLink(PURCHASED, storageNode, s.received, MOVEMENT_COLORS.RECEIPT_TO_STORAGE);
      }

      // depth 3 outflow: issued to asset groups / assets.
      const byTarget = new Map();
      (issuesByStorage.get(s.storageId) || []).forEach((r) => {
        const node = targetNodeFor(r.category, r.assetId, r.assetCode);
        byTarget.set(node, (byTarget.get(node) || 0) + (r.quantity || 0));
      });
      byTarget.forEach((value, node) => addLink(storageNode, node, value, MOVEMENT_COLORS.ISSUE));

      // depth 4 outflow: closing balance retained in THIS tank (its own node).
      if (s.actualClosing !== null && s.actualClosing > 0) {
        const close = ensureNode(`Closing · ${s.storageName}`, 4, MOVEMENT_COLORS.CLOSING_STOCK, { type: 'CLOSING', id: s.storageId }, 'left');
        addLink(storageNode, close, s.actualClosing, MOVEMENT_COLORS.CLOSING_STOCK);
      }
    });

    // Parallel line: fuel purchased directly into assets (never via a tank).
    const directByTarget = new Map();
    filteredMileage
      .filter((r) => r.isMarket && (!r.assetId || !allStorageIds.has(r.assetId)))
      .filter((r) => matchMulti(fAssetType, r.assetType))
      .filter((r) => matchMulti(fAsset, r.assetId))
      .forEach((r) => {
        if (r.quantity === null || r.quantity <= 0) return;
        const node = targetNodeFor(r.category, r.assetId, r.assetCode);
        directByTarget.set(node, (directByTarget.get(node) || 0) + r.quantity);
      });
    if (directByTarget.size) {
      ensureNode(DIRECT, 1, MOVEMENT_COLORS.DIRECT, { type: 'DIRECT' });
      directByTarget.forEach((value, node) => addLink(DIRECT, node, value, MOVEMENT_COLORS.DIRECT));
    }

    return { nodes, links, nodeMeta, otherCount };
  }, [storageSummary, filteredIssues, filteredMileage, allStorageIds, fAssetType, fAsset, sankeyMode]);

  // --- Sankey chart lifecycle --------------------------------------------
  // Keep the latest interaction handlers in refs so the chart's click handlers
  // (bound once per data change) always call the current state setters.
  const handlersRef = useRef({});
  handlersRef.current = {
    onStorage: (id) => {
      setFStorage([id]);
      setActiveTab('storage');
    },
    onCategory: (cat) => {
      setFAssetType((prev) => (prev.includes(cat) ? prev : [...prev, cat]));
      setSankeyMode('ASSET');
      setActiveTab('asset');
    },
    onAsset: (id) => {
      if (id) setAssetDrawer({ assetId: id });
      setActiveTab('asset');
    },
  };

  useEffect(() => {
    if (!chartDivRef.current) return undefined;

    // Dispose any previous chart before drawing a new one.
    if (chartRef.current) {
      chartRef.current.dispose();
      chartRef.current = null;
    }
    if (!sankey.nodes.length) return undefined;

    const chart = echarts.init(chartDivRef.current);
    chart.setOption({
      tooltip: {
        trigger: 'item',
        triggerOn: 'mousemove',
        confine: true,
        formatter: (p) => {
          if (p.dataType === 'edge') {
            return `${p.data.source} → ${p.data.target}<br/><b>${fmtL(p.data.value)} L</b>`;
          }
          return `<b>${p.name}</b><br/>${fmtL(p.value)} L`;
        },
      },
      series: [
        {
          type: 'sankey',
          left: 12,
          right: 170,
          top: 14,
          bottom: 14,
          nodeWidth: 16,
          nodeGap: 12,
          draggable: true,
          // node.depth pins the column; layout only optimises vertical order.
          emphasis: { focus: 'adjacency' },
          data: sankey.nodes,
          links: sankey.links,
          label: { fontSize: 11, color: '#333', overflow: 'truncate', width: 150 },
          lineStyle: { curveness: 0.5 },
        },
      ],
    });

    chart.on('click', (params) => {
      if (params.dataType !== 'node') return;
      const meta = sankey.nodeMeta[params.name];
      if (!meta) return;
      const h = handlersRef.current;
      if (meta.type === 'STORAGE') h.onStorage(meta.id);
      else if (meta.type === 'CATEGORY') h.onCategory(meta.id);
      else if (meta.type === 'ASSET') h.onAsset(meta.id);
    });

    // ECharts does not auto-resize; observe the container (covers window resize
    // AND the full-screen toggle, which only changes the wrapper size).
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => chart.resize());
      ro.observe(chartDivRef.current);
    }

    chartRef.current = chart;
    return () => {
      if (ro) ro.disconnect();
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, [sankey]);

  // Dispose on unmount.
  useEffect(
    () => () => {
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    },
    []
  );

  // Esc exits the full-screen Sankey. The ResizeObserver above re-fits the chart
  // when the wrapper changes size, so toggling full screen needs no re-create.
  useEffect(() => {
    if (!sankeyFullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setSankeyFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sankeyFullscreen]);

  // --- CSV exports --------------------------------------------------------
  const stamp = queriedRange
    ? `${queriedRange[0].format('YYYYMMDD')}_${queriedRange[1].format('YYYYMMDD')}`
    : moment().format('YYYYMMDD');

  // Null/undefined → '' for CSV (a true 0 is preserved, never blanked).
  const nz = (v) => (v === null || v === undefined ? '' : v);
  const csvDate = (v) => (v ? moment(v).format('YYYY-MM-DD HH:mm') : '');

  // Clears the consumer-side filters (sub-location / asset type / asset). The
  // Register Reconciliation tab offers this when one of them is set: a tank's
  // closing balance is a physical stock level and cannot be split by client or
  // by receiving machine.
  // A sub-location is meaningless without its location, so dropping the location
  // drops it too rather than leaving a filter nobody can see the basis for.
  const setLocationFilter = (next) => {
    setFLocation(next);
    if (!next.length) setFSubLocation([]);
  };

  const clearConsumerFilters = () => {
    setFSubLocation([]);
    setFAssetType([]);
    setFAsset([]);
  };

  const resetFilters = () => {
    setFLocation([]);
    setFSubLocation([]);
    setFStorage([]);
    setFAssetType([]);
    setFAsset([]);
    const diesel = fuelTypeOptions.find((f) => f && f.includes('DIESEL'));
    setFFuelType(diesel || fuelTypeOptions[0] || null);
  };

  // --- PDF report ---------------------------------------------------------
  // Builds a landscape PDF: header + KPI summary cards + Sankey image
  // (captured from the live ECharts instance) + Storage Summary, Fuel Issues,
  // and Fuel Receipts (grouped by Received In) tables. Uses the report's
  // already-filtered derived data, so it matches what is on screen.
  const handleExportPdf = useCallback(() => {
    if (!storageSummary.length && !assetIssues.length && !receiptsTable.length) {
      message.warning('Nothing to export — fetch data first.');
      return;
    }
    const SLATE = [52, 73, 94];
    const GREEN = [56, 158, 13];
    const BLUE = [24, 144, 255];
    const PURPLE = [114, 46, 209];

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const M = 12;
    let y = 0;

    const newPageIfNeeded = (needed) => {
      if (y + needed > pageH - M) {
        doc.addPage();
        y = M;
      }
    };
    const sectionHeader = (title, color = SLATE) => {
      newPageIfNeeded(16);
      doc.setFillColor(color[0], color[1], color[2]);
      doc.rect(M, y, pageW - M * 2, 7, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text(title, M + 3, y + 4.9);
      y += 10;
    };

    // --- Title bar ---
    doc.setFillColor(SLATE[0], SLATE[1], SLATE[2]);
    doc.rect(0, 0, pageW, 22, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('Fuel Stock & Issue Report', M, 12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const periodStr = queriedRange ? `${queriedRange[0].format('DD MMM YYYY')} - ${queriedRange[1].format('DD MMM YYYY')}` : '-';
    doc.text(`Period: ${periodStr}      Fuel Type: ${fFuelType || 'All'}      Generated: ${moment().format('DD MMM YYYY HH:mm')}`, M, 18);
    y = 27;

    const filt = [];
    if (fLocation.length) filt.push(`Location: ${fLocation.join(', ')}`);
    if (fSubLocation.length) filt.push(`Sub Location: ${fSubLocation.join(', ')}`);
    if (fStorage.length) filt.push(`Storage: ${fStorage.length} selected`);
    if (fAssetType.length) filt.push(`Asset Type: ${fAssetType.join(', ')}`);
    if (fAsset.length) filt.push(`Asset: ${fAsset.length} selected`);
    if (filt.length) {
      doc.setTextColor(120, 120, 120);
      doc.setFontSize(8);
      doc.text(`Filters — ${filt.join('   |   ')}`, M, y);
      y += 5;
    }

    // --- KPI cards ---
    const gap = 4;
    const cardW = (pageW - M * 2 - gap * 3) / 4;
    const cardH = 26;
    const cards = [
      { title: 'OPENING BALANCE IN STORAGE', value: summary.opening, color: SLATE, sub: [] },
      { title: 'TOTAL FUEL PURCHASED', value: summary.totalPurchased, color: GREEN, sub: [['Into storage', summary.received, GREEN], ['Direct into asset', summary.directToAsset, PURPLE]] },
      { title: 'TOTAL FUEL ISSUED', value: summary.totalIssuedToAssets, color: BLUE, sub: [['From storage', summary.issued, BLUE], ['Direct into asset', summary.directToAsset, PURPLE]] },
      { title: 'CLOSING BALANCE IN STORAGE', value: summary.closing, color: SLATE, sub: [] },
    ];
    cards.forEach((c, i) => {
      const x = M + i * (cardW + gap);
      doc.setDrawColor(225, 225, 225);
      doc.setFillColor(250, 250, 250);
      doc.roundedRect(x, y, cardW, cardH, 1.5, 1.5, 'FD');
      doc.setFillColor(c.color[0], c.color[1], c.color[2]);
      doc.rect(x, y, 1.6, cardH, 'F');
      doc.setTextColor(110, 110, 110);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.text(c.title, x + 4, y + 5);
      doc.setTextColor(c.color[0], c.color[1], c.color[2]);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.text(`${fmtL(c.value)} L`, x + 4, y + 12);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      let sy = y + 17.5;
      c.sub.forEach(([label, val, col]) => {
        doc.setTextColor(col[0], col[1], col[2]);
        doc.text(`${label}: ${fmtL(val)} L`, x + 4, sy);
        sy += 4;
      });
    });
    y += cardH + 8;

    // --- Sankey image (from the live ECharts instance) ---
    if (chartRef.current && sankey.nodes.length) {
      try {
        const url = chartRef.current.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#ffffff' });
        const cw = chartRef.current.getWidth() || 1000;
        const ch = chartRef.current.getHeight() || 480;
        sectionHeader('Fuel Flow (Sankey)');
        const imgW = pageW - M * 2;
        let imgH = imgW * (ch / cw);
        const maxH = pageH - y - M;
        if (imgH > maxH) imgH = maxH;
        doc.addImage(url, 'PNG', M, y, imgW, imgH, undefined, 'FAST');
        y += imgH + 6;
      } catch (e) {
        // chart capture failed — continue without the image.
      }
    }

    const tableBase = {
      theme: 'striped',
      styles: { fontSize: 7, cellPadding: 1.6, overflow: 'linebreak', lineColor: [230, 230, 230], lineWidth: 0.1, textColor: [50, 50, 50] },
      headStyles: { fillColor: SLATE, textColor: 255, fontStyle: 'bold', fontSize: 7, halign: 'left' },
      alternateRowStyles: { fillColor: [248, 249, 250] },
      margin: { left: M, right: M },
    };

    // --- Storage Summary ---
    sectionHeader('Storage Summary');
    autoTable(doc, {
      ...tableBase,
      startY: y,
      head: [['Storage Tank / Bowser', 'Asset Code', 'Location', 'Fuel', 'Opening (L)', 'Received (L)', 'Issued (L)', 'Closing (L)', 'Status']],
      body: storageSummary.map((s) => [s.storageName, s.assetCode, s.locationName, s.fuelType, fmtL(s.opening), fmtL(s.received), fmtL(s.issued), fmtL(s.actualClosing), s.status]),
      columnStyles: { 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' } },
    });
    y = doc.lastAutoTable.finalY + 8;

    // --- Fuel Receipts / Purchases, grouped by Received In ---
    sectionHeader('Fuel Receipts / Purchases  (grouped by Received In)', GREEN);
    const groups = [
      { key: 'Storage', label: 'Into Storage  (tank / bowser refills)' },
      { key: 'Direct to Asset', label: 'Direct to Asset' },
    ];
    groups.forEach((grp) => {
      const rows = receiptsTable.filter((r) => r.receivedInto === grp.key);
      if (!rows.length) return;
      const subtotal = rows.reduce((sum, r) => sum + (r.quantity || 0), 0);
      newPageIfNeeded(22);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(GREEN[0], GREEN[1], GREEN[2]);
      doc.text(`${grp.label}  —  ${rows.length} receipt(s), ${fmtL(subtotal)} L`, M, y);
      y += 3.5;
      autoTable(doc, {
        ...tableBase,
        startY: y,
        head: [['Date', 'Voucher / Receipt', 'Fuel', 'Tank / Asset', 'Location', 'Quantity (L)', 'Rate', 'Amount']],
        body: rows.map((r) => [
          r.datetimeMs ? moment(r.datetimeMs).format('DD MMM YYYY HH:mm') : '-',
          r.voucherNo || r.receiptNumber || '-', r.fuelType, r.targetName, r.locationName,
          fmtL(r.quantity), fmtMoney(r.rate), r.quantity != null && r.rate != null ? fmtMoney(r.quantity * r.rate) : '-',
        ]),
        foot: [['', '', '', '', 'Subtotal', `${fmtL(subtotal)} L`, '', '']],
        footStyles: { fillColor: [235, 235, 235], textColor: [40, 40, 40], fontStyle: 'bold', halign: 'right' },
        columnStyles: { 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' } },
      });
      y = doc.lastAutoTable.finalY + 7;
    });

    // --- Fuel Issues ---
    sectionHeader('Fuel Issues  (per asset — total = from storage + direct)', BLUE);
    autoTable(doc, {
      ...tableBase,
      startY: y,
      head: [['Asset', 'Owner', 'Location', 'Sub Location', 'Type', 'Category', 'Fuel', 'Total Issued (L)', 'From Storage (L)', 'Direct (L)', 'Issues', 'Amount']],
      body: assetIssues.map((g) => [
        g.assetCode + (g.assetName ? `\n${g.assetName}` : ''),
        g.ownerCode || '-', g.locationName, g.subLocationLabel, g.assetType, g.category, g.fuelType,
        fmtL(g.total), fmtL(g.fromStorage), fmtL(g.direct), g.count, fmtMoney(g.amount),
      ]),
      // Right-aligned numeric columns: Total / From Storage / Direct / Issues / Amount.
      columnStyles: { 7: { halign: 'right' }, 8: { halign: 'right' }, 9: { halign: 'right' }, 10: { halign: 'right' }, 11: { halign: 'right' } },
    });
    y = doc.lastAutoTable.finalY + 8;

    // --- Page footers ---
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i += 1) {
      doc.setPage(i);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text('AssetPro — Fuel Stock & Issue Report', M, pageH - 5);
      doc.text(`Page ${i} of ${pageCount}`, pageW - M, pageH - 5, { align: 'right' });
    }

    doc.save(`fuel-stock-issue-report-${stamp}.pdf`);
    message.success('PDF report generated.');
  }, [summary, storageSummary, assetIssues, receiptsTable, sankey, queriedRange, fFuelType, fLocation, fSubLocation, fStorage, fAssetType, fAsset, stamp]);

  const hasData = storages.length > 0;

  // --- Reusable bits ------------------------------------------------------
  const renderMultiSelect = (placeholder, value, onChange, options, disabled = false) => (
    <Select
      mode="multiple"
      allowClear
      disabled={disabled}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      style={{ width: '100%' }}
      maxTagCount={2}
      showSearch
      optionFilterProp="children"
      filterOption={(input, opt) => String(opt.props.children).toLowerCase().includes(input.toLowerCase())}
    >
      {options.map((o) => (
        <Option key={o} value={o}>{o}</Option>
      ))}
    </Select>
  );

  const statusTag = (status) => (status === 'OK' ? <Tag color="green">OK</Tag> : <Tag color="red">{status}</Tag>);

  const sevTag = (s) => <Tag color={s === 'High' ? 'red' : s === 'Medium' ? 'orange' : 'blue'}>{s}</Tag>;

  // --- Column sets --------------------------------------------------------
  // Every column below supports sorting and filtering (text-search for free-text
  // / numeric fields via searchProps; checkbox filters for categorical ones).
  const storageColumns = [
    {
      title: 'Storage Tank / Bowser', dataIndex: 'storageName', key: 'storageName', width: 200,
      sorter: strSorter('storageName'), ...searchProps('storageName', (r) => `${r.storageName} ${r.assetCode}`),
      render: (v, r) => (
        <div style={{ lineHeight: 1.3 }}>
          <span style={{ fontWeight: 600 }}>{v}</span>
          <div style={{ fontSize: 12, color: '#888', fontFamily: 'monospace' }}>{r.assetCode}</div>
        </div>
      ),
    },
    { title: 'Location', dataIndex: 'locationName', key: 'locationName', width: 130, sorter: strSorter('locationName'), ...searchProps('locationName') },
    {
      title: 'Fuel', dataIndex: 'fuelType', key: 'fuelType', width: 130, sorter: strSorter('fuelType'), ...searchProps('fuelType'),
      // A tank with no movement in the period has no fuel type to observe, so the
      // label comes from its asset model. Marked, never passed off as observed.
      render: (v, r) => (r.fuelTypeSource === 'asset model' ? (
        <Tooltip title="No fuel movement for this tank in the selected period — fuel type shown is the tank's configured type from its asset model, not one read off a transaction.">
          <span>{v} <Tag color="blue" style={{ transform: 'scale(0.85)', marginLeft: 2 }}>configured</Tag></span>
        </Tooltip>
      ) : v),
    },
    { title: 'Opening (L)', dataIndex: 'opening', key: 'opening', width: 110, align: 'right', sorter: numSorter('opening'), ...searchProps('opening'), render: (v) => (v === null ? <Tag color="red">Missing</Tag> : fmtL(v)) },
    { title: 'Received (L)', dataIndex: 'received', key: 'received', width: 110, align: 'right', sorter: numSorter('received'), ...searchProps('received'), render: (v) => fmtL(v) },
    { title: 'Issued (L)', dataIndex: 'issued', key: 'issued', width: 110, align: 'right', sorter: numSorter('issued'), ...searchProps('issued'), render: (v) => fmtL(v) },
    { title: 'Closing (L)', dataIndex: 'actualClosing', key: 'actualClosing', width: 130, align: 'right', sorter: numSorter('actualClosing'), ...searchProps('actualClosing'), render: (v) => (v === null ? <Tag color="red">Missing</Tag> : fmtL(v)) },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 160, sorter: strSorter('status'), ...enumFilter('status', [{ text: 'OK', value: 'OK' }, { text: 'Missing Opening / Closing', value: 'Missing Opening / Closing' }]), render: (v) => statusTag(v) },
  ];

  const assetIssueColumns = [
    {
      title: 'Asset', dataIndex: 'assetCode', key: 'assetCode', width: 200,
      sorter: strSorter('assetCode'), ...searchProps('assetCode', (r) => `${r.assetCode} ${r.assetName || ''} ${r.ownerCode || ''}`),
      render: (v, r) => (
        <div style={{ lineHeight: 1.3 }}>
          <Button type="link" size="small" style={{ padding: 0, height: 'auto', fontFamily: 'monospace', fontWeight: 600 }} onClick={() => setAssetDrawer({ assetId: r.assetId || `code:${r.assetCode}` })}>{v}</Button>
          {(r.assetName || r.ownerCode) && (
            <div style={{ fontSize: 12, color: '#888' }}>{[r.assetName, r.ownerCode && `Owner: ${r.ownerCode}`].filter(Boolean).join(' · ')}</div>
          )}
        </div>
      ),
    },
    { title: 'Location', dataIndex: 'locationName', key: 'locationName', width: 120, sorter: strSorter('locationName'), ...searchProps('locationName') },
    { title: 'Sub Location', dataIndex: 'subLocationLabel', key: 'subLocationLabel', width: 140, ellipsis: true, sorter: strSorter('subLocationLabel'), ...searchProps('subLocationLabel') },
    { title: 'Asset Type', dataIndex: 'assetType', key: 'assetType', width: 120, sorter: strSorter('assetType'), ...searchProps('assetType') },
    { title: 'Category', dataIndex: 'category', key: 'category', width: 130, sorter: strSorter('category'), ...searchProps('category') },
    { title: 'Source Tank/Bowser', dataIndex: 'sourceLabel', key: 'sourceLabel', width: 170, ellipsis: true, sorter: strSorter('sourceLabel'), ...searchProps('sourceLabel') },
    { title: 'Fuel', dataIndex: 'fuelType', key: 'fuelType', width: 80, sorter: strSorter('fuelType'), ...searchProps('fuelType') },
    { title: 'Total Issued (L)', dataIndex: 'total', key: 'total', width: 130, align: 'right', sorter: numSorter('total'), defaultSortOrder: 'descend', ...searchProps('total'), render: (v) => <strong>{fmtL(v)}</strong> },
    { title: 'From Storage (L)', dataIndex: 'fromStorage', key: 'fromStorage', width: 140, align: 'right', sorter: numSorter('fromStorage'), ...searchProps('fromStorage'), render: (v) => <span style={{ color: '#1890ff' }}>{fmtL(v)}</span> },
    { title: 'Direct (L)', dataIndex: 'direct', key: 'direct', width: 120, align: 'right', sorter: numSorter('direct'), ...searchProps('direct'), render: (v) => <span style={{ color: '#722ed1' }}>{fmtL(v)}</span> },
    { title: 'Issues', dataIndex: 'count', key: 'count', width: 80, align: 'right', sorter: numSorter('count'), ...searchProps('count') },
    { title: 'Avg Rate', dataIndex: 'avgRate', key: 'avgRate', width: 100, align: 'right', sorter: numSorter('avgRate'), ...searchProps('avgRate'), render: (v) => fmtMoney(v) },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', width: 120, align: 'right', sorter: numSorter('amount'), ...searchProps('amount'), render: (v) => fmtMoney(v) },
    { title: 'Faulty Meter', dataIndex: 'faulty', key: 'faulty', width: 100, align: 'right', sorter: numSorter('faulty'), ...searchProps('faulty'), render: (v) => (v > 0 ? <Tag color="orange">{v}</Tag> : v) },
    { title: 'Filled To Full', dataIndex: 'filledToFull', key: 'filledToFull', width: 110, align: 'right', sorter: numSorter('filledToFull'), ...searchProps('filledToFull') },
  ];

  const receiptColumns = [
    { title: 'Date', dataIndex: 'datetimeMs', key: 'datetimeMs', width: 150, sorter: numSorter('datetimeMs'), defaultSortOrder: 'descend', ...searchProps('datetimeMs', (r) => csvDate(r.datetimeMs)), render: (v) => (v ? moment(v).format('DD MMM YYYY HH:mm') : '—') },
    { title: 'Voucher / Receipt', dataIndex: 'voucherNo', key: 'voucherNo', width: 150, sorter: strSorter('voucherNo'), ...searchProps('voucherNo', (r) => `${r.voucherNo || ''} ${r.receiptNumber || ''}`), render: (v, r) => v || r.receiptNumber || '—' },
    { title: 'Fuel', dataIndex: 'fuelType', key: 'fuelType', width: 90, sorter: strSorter('fuelType'), ...searchProps('fuelType') },
    {
      title: 'Received In', dataIndex: 'receivedInto', key: 'receivedInto', width: 130,
      sorter: strSorter('receivedInto'),
      ...enumFilter('receivedInto', [{ text: 'Into Storage', value: 'Storage' }, { text: 'Direct to Asset', value: 'Direct to Asset' }]),
      render: (v) => (v === 'Storage' ? <Tag color="geekblue">Into Storage</Tag> : <Tag color="purple">Direct to Asset</Tag>),
    },
    { title: 'Tank / Asset', dataIndex: 'targetName', key: 'targetName', width: 220, ellipsis: true, sorter: strSorter('targetName'), ...searchProps('targetName') },
    { title: 'Location', dataIndex: 'locationName', key: 'locationName', width: 120, sorter: strSorter('locationName'), ...searchProps('locationName') },
    { title: 'Sub Location', dataIndex: 'subLocation', key: 'subLocation', width: 130, sorter: strSorter('subLocation'), ...searchProps('subLocation') },
    { title: 'Quantity (L)', dataIndex: 'quantity', key: 'quantity', width: 120, align: 'right', sorter: numSorter('quantity'), ...searchProps('quantity'), render: (v) => (v === null ? <Tag color="red">Missing</Tag> : fmtL(v)) },
    { title: 'Rate', dataIndex: 'rate', key: 'rate', width: 90, align: 'right', sorter: numSorter('rate'), ...searchProps('rate'), render: (v) => fmtMoney(v) },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', width: 120, align: 'right', sorter: (a, b) => ((a.quantity || 0) * (a.rate || 0)) - ((b.quantity || 0) * (b.rate || 0)), render: (_, r) => (r.quantity !== null && r.rate !== null ? fmtMoney(r.quantity * r.rate) : '—') },
  ];

  const txnColumns = [
    { title: 'Date', dataIndex: 'datetimeMs', key: 'datetimeMs', width: 150, sorter: numSorter('datetimeMs'), defaultSortOrder: 'descend', ...searchProps('datetimeMs', (r) => csvDate(r.datetimeMs)), render: (v) => (v ? moment(v).format('DD MMM YYYY HH:mm') : '—') },
    {
      title: 'Voucher', dataIndex: 'voucherNo', key: 'voucherNo', width: 120, sorter: strSorter('voucherNo'), ...searchProps('voucherNo'),
      render: (v, r) => (v && r.id
        ? <a href={voucherUrl(r.id)} target="_blank" rel="noopener noreferrer" title="Open voucher in AssetPro">{v} <Icon type="link" style={{ fontSize: 11 }} /></a>
        : v || '—'),
    },
    { title: 'Receipt No', dataIndex: 'receiptNumber', key: 'receiptNumber', width: 110, sorter: strSorter('receiptNumber'), ...searchProps('receiptNumber'), render: (v) => v || '—' },
    {
      title: 'Photos', key: 'photos', width: 90, align: 'center',
      render: (_, r) => (r.photos && r.photos.length
        ? <Button size="small" icon="picture" onClick={() => setPhotoModal({ voucherNo: r.voucherNo, photos: r.photos })}>{r.photos.length}</Button>
        : '—'),
    },
    {
      title: 'Asset', dataIndex: 'assetCode', key: 'assetCode', width: 200,
      sorter: strSorter('assetCode'), ...searchProps('assetCode', (r) => `${r.assetCode} ${r.assetName || ''} ${r.ownerCode || ''}`),
      render: (v, r) => (
        <div style={{ lineHeight: 1.3 }}>
          <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{v}</span>
          {(r.assetName || r.ownerCode) && <div style={{ fontSize: 12, color: '#888' }}>{[r.assetName, r.ownerCode && `Owner: ${r.ownerCode}`].filter(Boolean).join(' · ')}</div>}
        </div>
      ),
    },
    { title: 'Fuel', dataIndex: 'fuelType', key: 'fuelType', width: 80, sorter: strSorter('fuelType'), ...searchProps('fuelType') },
    { title: 'Quantity (L)', dataIndex: 'quantity', key: 'quantity', width: 110, align: 'right', sorter: numSorter('quantity'), ...searchProps('quantity'), render: (v) => (v === null ? <Tag color="red">Missing</Tag> : fmtL(v)) },
    { title: 'Rate', dataIndex: 'rate', key: 'rate', width: 80, align: 'right', sorter: numSorter('rate'), ...searchProps('rate'), render: (v) => fmtMoney(v) },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', width: 110, align: 'right', sorter: (a, b) => ((a.quantity || 0) * (a.rate || 0)) - ((b.quantity || 0) * (b.rate || 0)), render: (_, r) => (r.quantity !== null && r.rate !== null ? fmtMoney(r.quantity * r.rate) : '—') },
    { title: 'Source', dataIndex: 'source', key: 'source', width: 130, sorter: strSorter('source'), ...enumFilter('source', [{ text: 'Bowser Issue', value: 'BOWSER_ISSUE' }, { text: 'Market Purchase', value: 'MARKET' }, { text: 'Unknown', value: 'UNKNOWN' }]), render: (v) => (v === 'BOWSER_ISSUE' ? <Tag color="blue">Bowser Issue</Tag> : v === 'MARKET' ? <Tag color="green">Market Purchase</Tag> : <Tag color="orange">Unknown</Tag>) },
    {
      title: 'Issued From Bowser', dataIndex: 'bowserSourceCode', key: 'bowserSourceCode', width: 150,
      sorter: strSorter('bowserSourceCode'), ...searchProps('bowserSourceCode', (r) => `${r.bowserSourceCode || ''} ${r.bowserSourceName || ''}`),
      render: (v, r) => (v
        ? <span style={{ fontFamily: 'monospace' }} title={r.bowserSourceName || ''}>{v}</span>
        : <span style={{ color: '#bbb' }}>—</span>),
    },
    {
      title: 'Meter Reading', key: 'meters', width: 160,
      sorter: (a, b) => ((a.meters && a.meters[0] && a.meters[0].reading) || 0) - ((b.meters && b.meters[0] && b.meters[0].reading) || 0),
      ...searchProps('meters', (r) => (r.meters || []).map((m) => `${m.name} ${m.reading}`).join(' ')),
      render: (_, r) => (
        r.meters && r.meters.length ? (
          <div style={{ lineHeight: 1.35 }}>
            {r.meters.map((m, i) => (
              <div key={i} style={{ whiteSpace: 'nowrap' }}>
                {m.name && <span style={{ color: '#888' }}>{m.name}: </span>}
                <span style={{ fontFamily: 'monospace' }}>{fmtMeter(m.reading)}</span>
                {m.faulty && <Tag color="orange" style={{ marginLeft: 4, transform: 'scale(0.85)' }}>faulty</Tag>}
              </div>
            ))}
          </div>
        ) : '—'
      ),
    },
    { title: 'Faulty Meter', dataIndex: 'isFaultyMeter', key: 'isFaultyMeter', width: 110, ...enumFilter('isFaultyMeter', [{ text: 'Yes', value: true }, { text: 'No', value: false }]), render: (v) => (v === true ? <Tag color="orange">Yes</Tag> : v === false ? 'No' : '—') },
    { title: 'Filled To Full', dataIndex: 'isFilledToFull', key: 'isFilledToFull', width: 110, ...enumFilter('isFilledToFull', [{ text: 'Yes', value: true }, { text: 'No', value: false }]), render: (v) => (v === true ? 'Yes' : v === false ? 'No' : '—') },
    { title: 'Location', dataIndex: 'locationName', key: 'locationName', width: 120, sorter: strSorter('locationName'), ...searchProps('locationName') },
    { title: 'Sub Location', dataIndex: 'subLocation', key: 'subLocation', width: 130, sorter: strSorter('subLocation'), ...searchProps('subLocation') },
  ];

  const exceptionColumns = [
    { title: 'Severity', dataIndex: 'severity', key: 'severity', width: 100, sorter: (a, b) => ({ High: 0, Medium: 1, Low: 2 }[a.severity] - { High: 0, Medium: 1, Low: 2 }[b.severity]), ...enumFilter('severity', [{ text: 'High', value: 'High' }, { text: 'Medium', value: 'Medium' }, { text: 'Low', value: 'Low' }]), render: (v) => sevTag(v) },
    { title: 'Type', dataIndex: 'type', key: 'type', width: 240, sorter: strSorter('type'), ...searchProps('type') },
    { title: 'Detail', dataIndex: 'detail', key: 'detail', sorter: strSorter('detail'), ...searchProps('detail') },
  ];

  // Asset drawer transactions for the selected asset — both fuel issued from a
  // bowser and fuel purchased directly, each tagged with its source.
  const drawerIssueRows = useMemo(() => {
    if (!assetDrawer) return [];
    const id = assetDrawer.assetId;
    const storage = filteredIssues
      .filter((r) => (r.assetId || `code:${r.assetCode}`) === id)
      .map((r) => ({ key: r.key, assetCode: r.assetCode, datetimeMs: r.datetimeMs, kind: 'Storage', sourceLabel: r.storageName, quantity: r.quantity, rate: r.rate, voucherNo: r.voucherNo, isFaultyMeter: r.isFaultyMeter }));
    const direct = filteredMileage
      .filter((r) => r.isMarket && (!r.assetId || !allStorageIds.has(r.assetId)))
      .filter((r) => (r.assetId || `code:${r.assetCode}`) === id)
      .map((r) => ({ key: r.key, assetCode: r.assetCode, datetimeMs: r.datetimeMs, kind: 'Direct', sourceLabel: 'Direct purchase', quantity: r.quantity, rate: r.rate, voucherNo: r.voucherNo, isFaultyMeter: r.isFaultyMeter }));
    return [...storage, ...direct].sort((a, b) => (b.datetimeMs || 0) - (a.datetimeMs || 0));
  }, [assetDrawer, filteredIssues, filteredMileage, allStorageIds]);


  return (
    <div style={{ padding: 16 }}>
      <Card title={<span><Icon type="dashboard" /> Fuel Stock &amp; Issue Report</span>} style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} type="flex" align="middle">
          <Col>
            <span style={{ marginRight: 8, color: '#666' }}>Period:</span>
            <Radio.Group
              value={periodPreset}
              style={{ marginRight: 8 }}
              onChange={(e) => {
                const k = e.target.value;
                const preset = PERIOD_PRESETS.find((p) => p.key === k);
                if (preset) setRange(preset.range());
                setPeriodPreset(k);
              }}
            >
              {PERIOD_PRESETS.map((p) => (
                <Radio.Button key={p.key} value={p.key}>{p.label}</Radio.Button>
              ))}
            </Radio.Group>
            <RangePicker
              value={range}
              onChange={(v) => {
                setRange(v);
                setPeriodPreset(''); // manual edit -> custom
              }}
              format="YYYY-MM-DD"
              allowClear={false}
            />
          </Col>
          <Col>
            <Button type="primary" icon="search" loading={loading} onClick={handleFetch}>Fetch</Button>
          </Col>
          <Col>
            <Button icon="file-pdf" disabled={!hasData} onClick={handleExportPdf}>Download PDF Report</Button>
          </Col>
        </Row>

        {hasData && (
          <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select placeholder="Fuel Type" value={fFuelType || undefined} onChange={setFFuelType} style={{ width: '100%' }} allowClear showSearch optionFilterProp="children">
                {fuelTypeOptions.map((o) => <Option key={o} value={o}>{o}</Option>)}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>{renderMultiSelect('Location (all)', fLocation, setLocationFilter, locationOptions)}</Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Tooltip title={fLocation.length ? '' : 'Pick a Location first — a sub-location only has meaning inside one.'}>
                {renderMultiSelect(
                  fLocation.length ? 'Sub Location / Client (all)' : 'Sub Location — pick a Location first',
                  fSubLocation,
                  setFSubLocation,
                  subLocationOptions,
                  fLocation.length === 0,
                )}
              </Tooltip>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select mode="multiple" allowClear placeholder="Storage Tank / Bowser (all)" value={fStorage} onChange={setFStorage} style={{ width: '100%' }} maxTagCount={1} showSearch optionFilterProp="children" filterOption={(input, opt) => String(opt.props.children).toLowerCase().includes(input.toLowerCase())}>
                {storageChoices.map((s) => <Option key={s.storageId} value={s.storageId}>{`${s.storageName} (${s.assetCode})`}</Option>)}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>{renderMultiSelect('Asset Type (all)', fAssetType, setFAssetType, assetTypeOptions)}</Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select mode="multiple" allowClear placeholder="Asset — code / name / owner (all)" value={fAsset} onChange={setFAsset} style={{ width: '100%' }} maxTagCount={1} showSearch optionLabelProp="label" filterOption={(input, opt) => String(opt.props.title || '').toLowerCase().includes(input.toLowerCase())}>
                {assetChoices.map((c) => (
                  <Option key={c.id} value={c.id} label={c.code} title={`${c.code} ${c.name || ''} ${c.owner || ''}`}>
                    <div style={{ lineHeight: 1.3 }}>
                      <span style={{ fontFamily: 'monospace' }}>{c.code}</span>
                      {(c.name || c.owner) && <div style={{ fontSize: 12, color: '#888' }}>{[c.name, c.owner && `Owner: ${c.owner}`].filter(Boolean).join(' · ')}</div>}
                    </div>
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Button icon="reload" onClick={resetFilters}>Reset Filters</Button>
            </Col>
          </Row>
        )}

        {fStorage.length === 1 && (
          <div style={{ marginTop: 12 }}>
            <Tag closable color="blue" onClose={() => setFStorage([])}>
              Filtered to storage: {storageChoices.find((s) => s.storageId === fStorage[0])?.storageName || fStorage[0]}
            </Tag>
          </div>
        )}

        {/* Sub location is a property of the fuel ENTRY (its client), not of a
            tank, so it scopes transactions only. Spelled out on screen so the
            tank stock figures are never read as client-attributed. */}
        {fSubLocation.length > 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message={`Sub location filter active: ${fSubLocation.join(', ')}`}
            description="Sub location (client) is read from the client on each fuel entry, so it scopes Fuel Issues, Receipts, Transactions and the issued side of the Sankey. Tank stock — Opening, Received, Issued and Closing in the KPI cards and the Storage Summary tab — stays tank-level, because a tank's stock cannot be attributed to a single sub location."
          />
        )}

        {error && (
          <Alert type="error" showIcon message="Failed to load report" description={error} style={{ marginTop: 12 }} closable onClose={() => setError(null)} />
        )}
        {fetchNotes && !error && (
          <Alert type="info" showIcon message={fetchNotes} style={{ marginTop: 12 }} />
        )}
      </Card>

      {hasData && (
        <>
          {/* Complete picture: Opening → Purchased → Issued → Closing, each
              total broken into its storage and direct-to-asset components. */}
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={24} sm={12} md={6}>
              <Card bodyStyle={{ padding: 14 }}>
                <Statistic title="Opening Balance in Storage" value={summary.opening} precision={2} suffix="L" />
                <div style={{ fontSize: 12, color: '#999', marginTop: 6 }}>Fuel held in tanks / bowsers at period start</div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card bodyStyle={{ padding: 14 }}>
                <Statistic title="Total Fuel Purchased" value={summary.totalPurchased} precision={2} suffix="L" valueStyle={{ color: '#389e0d' }} />
                <div style={{ fontSize: 15, marginTop: 8, lineHeight: 1.8, fontWeight: 500 }}>
                  <div style={{ color: '#52c41a' }}>Received into storage: {fmtL(summary.received)} L</div>
                  <div style={{ color: '#722ed1' }}>Direct into asset: {fmtL(summary.directToAsset)} L</div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card bodyStyle={{ padding: 14 }}>
                <Statistic title="Total Fuel Issued" value={summary.totalIssuedToAssets} precision={2} suffix="L" valueStyle={{ color: '#1890ff' }} />
                <div style={{ fontSize: 15, marginTop: 8, lineHeight: 1.8, fontWeight: 500 }}>
                  <div style={{ color: '#1890ff' }}>Issued from storage: {fmtL(summary.issued)} L</div>
                  <div style={{ color: '#722ed1' }}>Direct into asset: {fmtL(summary.directToAsset)} L</div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card bodyStyle={{ padding: 14 }}>
                <Statistic title="Closing Balance in Storage" value={summary.closing} precision={2} suffix="L" />
                <div style={{ fontSize: 12, color: '#999', marginTop: 6 }}>Fuel held in tanks / bowsers at period end</div>
              </Card>
            </Col>
          </Row>

          {(summary.missingOpening > 0 || summary.missingClosing > 0 || summary.missingPurchaseQty > 0) && (
            <Alert
              style={{ marginBottom: 16 }}
              type="warning"
              showIcon
              message="Data reliability notes"
              description={
                <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                  {summary.missingOpening > 0 && <li>{summary.missingOpening} tank(s) have no opening balance — excluded from the opening total (not counted as zero).</li>}
                  {summary.missingClosing > 0 && <li>{summary.missingClosing} tank(s) have no closing balance — excluded from the closing total (not counted as zero).</li>}
                  {summary.missingPurchaseQty > 0 && <li>{summary.missingPurchaseQty} market-purchase entr(y/ies) have a missing quantity — excluded from Total Purchased.</li>}
                </ul>
              }
            />
          )}

          <Card title={<span><Icon type="apartment" /> Fuel Flow (Sankey)</span>} style={{ marginBottom: 16 }}>
            <div
              style={
                sankeyFullscreen
                  ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000, background: '#fff', padding: 16, overflow: 'auto', display: 'flex', flexDirection: 'column' }
                  : {}
              }
            >
              <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                <Tooltip title="Total Issued shows one combined node (use when there are too many categories); Grouped splits by asset category; Asset drills into the top 25 assets.">
                  <Select size="small" value={sankeyMode} onChange={setSankeyMode} style={{ width: 230 }}>
                    <Option value="TOTAL">Total Issued (single node)</Option>
                    <Option value="GROUPED">Grouped (by category)</Option>
                    <Option value="ASSET">Asset Drill-down (top 25)</Option>
                  </Select>
                </Tooltip>
                <Button
                  size="small"
                  icon={sankeyFullscreen ? 'fullscreen-exit' : 'fullscreen'}
                  onClick={() => setSankeyFullscreen((v) => !v)}
                >
                  {sankeyFullscreen ? 'Exit full screen' : 'Full screen'}
                </Button>
                {sankeyFullscreen && <span style={{ color: '#999', fontSize: 12 }}>Press Esc to exit</span>}
              </div>

              <div style={{ marginBottom: 8 }}>
                {Object.entries({ 'Opening Balance → Storage': MOVEMENT_COLORS.OPENING, 'Fuel Purchased → Storage': MOVEMENT_COLORS.RECEIPT_TO_STORAGE, 'Storage → Asset (issued)': MOVEMENT_COLORS.ISSUE, 'Direct Purchase → Asset': MOVEMENT_COLORS.DIRECT, 'Storage → Closing Balance': MOVEMENT_COLORS.CLOSING_STOCK }).map(([label, color]) => (
                  <Tag key={label} color={color} style={{ color: '#fff' }}>{label}</Tag>
                ))}
                <div style={{ marginTop: 4, color: '#999', fontSize: 12 }}>
                  Flow: Opening Balance + Fuel Purchased → each Storage Unit → issued to assets + Closing Balance. Direct purchases feed the asset groups without passing through a tank.
                  {sankeyMode === 'ASSET' && sankey.otherCount > 0 && ` Showing top ${TOP_ASSETS} assets; ${sankey.otherCount} more grouped as "Other Assets".`}
                  {' '}Click a storage node to filter · a category to drill into assets · an asset for its transactions.
                </div>
              </div>

              {sankey.nodes.length ? (
                <div ref={chartDivRef} style={{ width: '100%', height: sankeyFullscreen ? 'calc(100vh - 150px)' : 480, flex: sankeyFullscreen ? 1 : 'none' }} />
              ) : (
                <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>No fuel movement to chart for the current filters.</div>
              )}
            </div>
          </Card>

          <Card>
            <Tabs activeKey={activeTab} onChange={setActiveTab}>
              <TabPane tab={`Storage Summary (${storageSummary.length})`} key="storage">
                <ExportableTable
                  columns={storageColumns}
                  dataSource={storageSummary}
                  rowKey="storageId"
                  size="middle"
                  loading={loading}
                  scroll={{ x: 1500 }}
                  pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'] }}
                  exportName={`fuel-storage-summary-${stamp}.csv`}
                  exportHeader={['Storage Tank / Bowser', 'Asset Code', 'Location', 'Fuel Type', 'Opening (L)', 'Received (L)', 'Issued (L)', 'Closing (L)', 'Status']}
                  exportRow={(s) => [s.storageName, s.assetCode, s.locationName, s.fuelType, nz(s.opening), nz(s.received), nz(s.issued), nz(s.actualClosing), s.status]}
                />
              </TabPane>

              <TabPane tab={`Fuel Receipts / Purchases (${receiptsTable.length})`} key="receipts">
                <div style={{ marginBottom: 8, color: '#999', fontSize: 12 }}>
                  Market purchases in the period — both refills into storage tanks/bowsers and fuel received directly into assets. Sums to Total Purchased ({fmtL(summary.purchased)} L: {fmtL(summary.receivedIntoStorageMileage)} L into storage · {fmtL(summary.directToAsset)} L direct).
                </div>
                <ExportableTable
                  columns={receiptColumns}
                  dataSource={receiptsTable}
                  rowKey="key"
                  size="middle"
                  scroll={{ x: 1450 }}
                  pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'] }}
                  exportName={`fuel-receipts-${stamp}.csv`}
                  exportHeader={['Date', 'Voucher', 'Receipt No', 'Fuel', 'Received In', 'Tank / Asset', 'Location', 'Sub Location', 'Quantity (L)', 'Rate', 'Amount']}
                  exportRow={(r) => [csvDate(r.datetimeMs), r.voucherNo, r.receiptNumber, r.fuelType, r.receivedInto, r.targetName, r.locationName, r.subLocation, nz(r.quantity), nz(r.rate), r.quantity !== null && r.rate !== null ? (r.quantity * r.rate).toFixed(2) : '']}
                />
              </TabPane>

              <TabPane tab={`Fuel Issues (${assetIssues.length})`} key="asset">
                <div style={{ marginBottom: 8, color: '#999', fontSize: 12 }}>
                  Total fuel issued into assets in the period — {fmtL(issuesTotals.total)} L
                  (<span style={{ color: '#1890ff' }}>from storage {fmtL(issuesTotals.fromStorage)} L</span> ·{' '}
                  <span style={{ color: '#722ed1' }}>direct {fmtL(issuesTotals.direct)} L</span>). Each row is the asset's monthly total with its from-storage / direct split.
                </div>
                <ExportableTable
                  columns={assetIssueColumns}
                  dataSource={assetIssues}
                  rowKey="key"
                  size="middle"
                  scroll={{ x: 1890 }}
                  pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'] }}
                  exportName={`fuel-issues-${stamp}.csv`}
                  exportHeader={['Asset Code', 'Alt Name', 'Owner Code', 'Location', 'Sub Location', 'Asset Type', 'Category', 'Source Tank/Bowser', 'Fuel', 'Total Issued (L)', 'From Storage (L)', 'Direct (L)', 'Issues', 'Avg Rate', 'Amount', 'Faulty Meter', 'Filled To Full']}
                  exportRow={(g) => [g.assetCode, g.assetName, g.ownerCode, g.locationName, g.subLocationLabel, g.assetType, g.category, g.sourceLabel, g.fuelType, nz(g.total), nz(g.fromStorage), nz(g.direct), nz(g.count), nz(g.avgRate), nz(g.amount), nz(g.faulty), nz(g.filledToFull)]}
                />
              </TabPane>

              <TabPane tab={`Fuel Transactions (${filteredMileage.length})`} key="txn">
                <ExportableTable
                  columns={txnColumns}
                  dataSource={filteredMileage}
                  rowKey="key"
                  size="small"
                  scroll={{ x: 2290 }}
                  pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100', '200'] }}
                  exportName={`fuel-transactions-${stamp}.csv`}
                  exportHeader={['Date', 'Voucher', 'Voucher URL', 'Receipt No', 'Asset Code', 'Alt Name', 'Owner Code', 'Fuel', 'Quantity (L)', 'Rate', 'Amount', 'Source', 'Issued From Bowser', 'Meter Reading', 'Faulty Meter', 'Filled To Full', 'Location', 'Sub Location', 'Photo URLs']}
                  exportRow={(r) => [csvDate(r.datetimeMs), r.voucherNo, voucherUrl(r.id) || '', r.receiptNumber, r.assetCode, r.assetName, r.ownerCode, r.fuelType, nz(r.quantity), nz(r.rate), r.quantity !== null && r.rate !== null ? (r.quantity * r.rate).toFixed(2) : '', r.source === 'BOWSER_ISSUE' ? 'Bowser Issue' : r.source === 'MARKET' ? 'Market Purchase' : 'Unknown', r.bowserSourceCode || '', (r.meters || []).map((m) => `${m.name ? `${m.name}: ` : ''}${m.reading != null ? m.reading : ''}`).join(' | '), r.isFaultyMeter === true ? 'Yes' : r.isFaultyMeter === false ? 'No' : '', r.isFilledToFull === true ? 'Yes' : r.isFilledToFull === false ? 'No' : '', r.locationName, r.subLocation, (r.photos || []).map((p) => p.url).join(' | ')]}
                />
              </TabPane>

              <TabPane tab="Reconciliation" key="recon">
                {renderReconciliation(summary, storageSummary)}
              </TabPane>

              <TabPane tab="Register Reconciliation" key="register">
                <RegisterReconciliationTab ctx={reconContext} onClearConsumerFilters={clearConsumerFilters} />
              </TabPane>

              <TabPane tab={<span>Exceptions {exceptions.length ? <Tag color={exceptions.some((e) => e.severity === 'High') ? 'red' : 'orange'}>{exceptions.length}</Tag> : null}</span>} key="exceptions">
                {exceptions.length ? (
                  <ExportableTable
                    columns={exceptionColumns}
                    dataSource={exceptions}
                    rowKey="key"
                    size="small"
                    pagination={{ pageSize: 25 }}
                    exportName={`fuel-exceptions-${stamp}.csv`}
                    exportHeader={['Severity', 'Type', 'Detail']}
                    exportRow={(e) => [e.severity, e.type, e.detail]}
                  />
                ) : (
                  <Alert type="success" showIcon message="No exceptions detected for the current filters." />
                )}
              </TabPane>
            </Tabs>
          </Card>
        </>
      )}

      {/* Asset transactions drawer */}
      <Drawer
        title={assetDrawer ? `Fuel issued to ${drawerIssueRows[0]?.assetCode || assetDrawer.assetId}` : 'Asset'}
        width={720}
        visible={!!assetDrawer}
        onClose={() => setAssetDrawer(null)}
      >
        {assetDrawer && (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}><Statistic title="Total Issued" value={drawerIssueRows.reduce((s, r) => s + (r.quantity || 0), 0)} precision={2} suffix="L" /></Col>
              <Col span={6}><Statistic title="From Storage" value={drawerIssueRows.filter((r) => r.kind === 'Storage').reduce((s, r) => s + (r.quantity || 0), 0)} precision={2} suffix="L" valueStyle={{ color: '#1890ff' }} /></Col>
              <Col span={6}><Statistic title="Direct" value={drawerIssueRows.filter((r) => r.kind === 'Direct').reduce((s, r) => s + (r.quantity || 0), 0)} precision={2} suffix="L" valueStyle={{ color: '#722ed1' }} /></Col>
              <Col span={6}><Statistic title="Transactions" value={drawerIssueRows.length} /></Col>
            </Row>
            <Table
              size="small"
              rowKey="key"
              dataSource={drawerIssueRows}
              pagination={{ pageSize: 20 }}
              columns={[
                { title: 'Date', dataIndex: 'datetimeMs', key: 'd', render: (v) => (v ? moment(v).format('DD MMM YYYY HH:mm') : '—') },
                { title: 'Type', dataIndex: 'kind', key: 'k', render: (v) => (v === 'Storage' ? <Tag color="blue">From Storage</Tag> : <Tag color="purple">Direct</Tag>) },
                { title: 'Source', dataIndex: 'sourceLabel', key: 's' },
                { title: 'Qty (L)', dataIndex: 'quantity', key: 'q', align: 'right', render: (v) => fmtL(v) },
                { title: 'Rate', dataIndex: 'rate', key: 'r', align: 'right', render: (v) => fmtMoney(v) },
                { title: 'Voucher', dataIndex: 'voucherNo', key: 'v', render: (v) => v || '—' },
                { title: 'Faulty', dataIndex: 'isFaultyMeter', key: 'f', render: (v) => (v === true ? <Tag color="orange">Yes</Tag> : '—') },
              ]}
            />
          </>
        )}
      </Drawer>

      {/* Voucher photos (mileage entry photos). */}
      <Modal
        title={photoModal ? `Voucher photos${photoModal.voucherNo ? ` — ${photoModal.voucherNo}` : ''}` : 'Voucher photos'}
        visible={!!photoModal}
        onCancel={() => setPhotoModal(null)}
        footer={null}
        width={820}
      >
        {photoModal && (
          photoModal.photos.length ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {photoModal.photos.map((p, i) => (
                <a key={p.url || i} href={p.url} target="_blank" rel="noopener noreferrer" title={p.name || 'Open full image'}>
                  <img
                    src={p.url}
                    alt={p.name || `photo-${i + 1}`}
                    style={{ width: 240, height: 240, objectFit: 'cover', border: '1px solid #eee', borderRadius: 4 }}
                  />
                </a>
              ))}
            </div>
          ) : (
            <Alert type="info" showIcon message="No photos attached to this voucher." />
          )
        )}
      </Modal>
    </div>
  );
}

// Overall + tank-wise reconciliation block (shared by the tab and the drawer).
function renderReconciliation(summary, storageSummary) {
  const line = (label, value, opts = {}) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: opts.total ? '2px solid #d9d9d9' : '1px solid #f0f0f0', fontWeight: opts.total ? 600 : 400 }}>
      <span style={{ color: opts.muted ? '#999' : undefined }}>{label}</span>
      <span style={{ color: opts.color, fontFamily: 'monospace' }}>{fmtL(value)} L</span>
    </div>
  );
  return (
    <div>
      <Card type="inner" title="Overall" style={{ marginBottom: 16 }}>
        {line('Opening Balance in Storage', summary.opening)}
        {line('+ Fuel Received into Storage', summary.received, { color: '#52c41a' })}
        {line('− Fuel Issued from Storage', summary.issued, { color: '#1890ff' })}
        {line('= Closing Balance in Storage', summary.closing, { total: true })}
        <div style={{ marginTop: 12, fontSize: 12, color: '#999' }}>
          Of total purchased {fmtL(summary.totalPurchased)} L: {fmtL(summary.received)} L into storage · {fmtL(summary.directToAsset)} L direct to assets.
        </div>
      </Card>
      <Card type="inner" title="Tank-wise">
        {storageSummary.map((s) => (
          <div key={s.storageId} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{s.storageName} <span style={{ color: '#999', fontWeight: 400, fontFamily: 'monospace' }}>{s.assetCode}</span></div>
            <div style={{ fontSize: 13, color: '#555' }}>
              Opening {fmtL(s.opening)} + Received {fmtL(s.received)} − Issued {fmtL(s.issued)} = Closing {fmtL(s.actualClosing)} L
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

export default FuelStockIssueReport;
